import apiGet from 'lib/network/apiGet'
import { useState, useEffect, useRef, useMemo } from 'react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, AreaChart, Area, LabelList } from 'recharts'
import { toUpper, isArray, toLower, startsWith, includes } from 'lodash'
import moment from 'moment'
import classNames from 'classnames'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { formatPrice } from 'lib/utils'
import Selection from 'components/molecules/Selection'
import Popover from 'components/molecules/Popover'
import { IoCalendar } from 'react-icons/io5'
import { DateRangePicker } from 'react-date-range'
import { enUS, arSA } from 'date-fns/locale'
import { defaultStaticRanges, defaultInputRanges } from 'react-date-range/dist/defaultRanges'
import { getLocalizedCountries } from 'pages/company/modals/countries'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import OperationFunnelLists from './OperationFunnelLists'

const inferPeriodFromRange = (start, end) => {
  const days = moment(end).diff(moment(start), 'days')
  if (days <= 14) return 'daily'
  if (days <= 90) return 'weekly'
  if (days <= 730) return 'monthly'
  return 'yearly'
}

const DashboardStats = () => {
  // const [params, setParams] = useState({ t: 1 })
  const [params, setParams] = useState({ t: 5 }) // Booking Statistics
  const [stats, setStats] = useState(null)
  const [growthData, setGrowthData] = useState([])
  const [activityData, setActivityData] = useState(null)
  const [companyStats, setCompanyStats] = useState(null)
  const [carStats, setCarStats] = useState(null)
  const [carPerformanceStats, setCarPerformanceStats] = useState(null)
  const [bookingStats, setBookingStats] = useState(null)
  const [bookingTrends, setBookingTrends] = useState(null)
  const [bookingPerformance, setBookingPerformance] = useState(null)
  const [transactionStats, setTransactionStats] = useState(null)
  const [revenueStats, setRevenueStats] = useState(null)
  const [deliveryStats, setDeliveryStats] = useState(null)
  const [discountStats, setDiscountStats] = useState(null)
  const [fileUploadStats, setFileUploadStats] = useState(null)
  const [fraudStats, setFraudStats] = useState(null)
  const [reviewStats, setReviewStats] = useState(null)
  const [notificationStats, setNotificationStats] = useState(null)
  const [activityLogStats, setActivityLogStats] = useState(null)
  const [dashboardOverview, setDashboardOverview] = useState(null)
  const [activeSessionStats, setActiveSessionStats] = useState(null)
  const [devicePlatformStats, setDevicePlatformStats] = useState(null)
  const [operationFunnelData, setOperationFunnelData] = useState(null)
  const [operationFunnelLoading, setOperationFunnelLoading] = useState(false)
  const [companies, setCompanies] = useState([])
  const [branches, setBranches] = useState([])
  const [marketSegments, setMarketSegments] = useState([])
  const [insuranceOptions, setInsuranceOptions] = useState([])
  const [fuelPolicyOptions, setFuelPolicyOptions] = useState([])
  const [loading, setLoading] = useState(true)
  const carDatePopoverRef = useRef(null)
  const globalDatePopoverRef = useRef(null)

  const defaultRangeStart = moment().startOf('month')
  const defaultRangeEnd = moment().endOf('day')
  const [globalDateRange, setGlobalDateRange] = useState({
    startDate: defaultRangeStart,
    endDate: defaultRangeEnd,
  })
  const [globalDateSelection, setGlobalDateSelection] = useState({
    startDate: defaultRangeStart.toDate(),
    endDate: defaultRangeEnd.toDate(),
    key: 'selection',
  })

  const getQueryDateParams = (rangeOverride = null) => {
    const source = rangeOverride || globalDateRange
    if (!source?.startDate || !source?.endDate) {
      return {}
    }

    const startMoment = moment(source.startDate).startOf('day')
    const endMoment = moment(source.endDate).endOf('day')
    const startDay = startMoment.format('YYYY-MM-DD')
    const endDay = endMoment.format('YYYY-MM-DD')
    return {
      startDate: `${startDay}T00:00:00.000Z`,
      endDate: `${endDay}T23:59:59.999Z`,
      period: inferPeriodFromRange(startMoment, endMoment),
    }
  }

  const [bookingFilters, setBookingFilters] = useState({
    companyIds: [],
    instantBooking: '',
    companyGovernorates: [],
    companyMinAge: '',
    companyMaxAge: '',
    blockedCountries: [],
    ajarleePercentageMin: '',
    ajarleePercentageMax: '',
    companyDepositMin: '',
    companyDepositMax: '',
    vatTax: '',
    delivery: '',
    deliveryFeeMin: '',
    deliveryFeeMax: '',
    source: '',
    platform: '',
    bookingStatus: '',
    paymentStatus: '',
  })
  const [companyFilters, setCompanyFilters] = useState({
    companyId: '',
    governorate: '',
  })
  const [carFilters, setCarFilters] = useState({
    startDate: '',
    endDate: '',
    companyId: '',
    branchId: '',
    marketSegment: '',
    status: '',
    insurance: '',
    fuelPolicy: '',
  })
  const [branchStatsFilters, setBranchStatsFilters] = useState({
    companyId: '',
    lang: '',
    type: '',
  })
  const { t, i18n } = useTranslation()
  const isArabic = startsWith(toLower(i18n.language || ''), 'ar')
  const [carDateRange, setCarDateRange] = useState({ startDate: null, endDate: null })
  const datePickerLocale = useMemo(() => (isArabic ? arSA : enUS), [isArabic])

  const localizedStaticRanges = useMemo(() => {
    const labels = {
      Today: t('reports.datePicker.today'),
      Yesterday: t('reports.datePicker.yesterday'),
      'This Week': t('reports.datePicker.thisWeek'),
      'Last Week': t('reports.datePicker.lastWeek'),
      'This Month': t('reports.datePicker.thisMonth'),
      'Last Month': t('reports.datePicker.lastMonth'),
    }
    return defaultStaticRanges.map(range => ({
      ...range,
      label: labels[range.label] || range.label,
    }))
  }, [t])

  const localizedInputRanges = useMemo(() => {
    return defaultInputRanges.map(range => {
      if (range.label === 'days up to today') {
        return { ...range, label: t('reports.datePicker.continuous') }
      }
      if (range.label === 'days starting today') {
        return { ...range, label: t('reports.datePicker.early') }
      }
      return range
    })
  }, [t])

  const tabs = [
    // { id: 1, name: t('statsDashboard.dashboardOverview'), href: '#', current: false },
    // { id: 2, name: t('statsDashboard.userStatistics'), href: '#', current: false },
    // { id: 3, name: t('statsDashboard.companyStatistics'), href: '#', current: false },
    // { id: 4, name: t('statsDashboard.carStatistics'), href: '#', current: false },
    { id: 5, name: t('statsDashboard.bookingStatistics'), href: '#', current: false },
    { id: 13, name: t('statsDashboard.dailyOperationFunnel'), href: '#', current: false },
    // { id: 6, name: t('statsDashboard.transactionRevenue'), href: '#', current: false },
    // { id: 7, name: t('statsDashboard.deliveryStatistics'), href: '#', current: false },
    // { id: 8, name: t('statsDashboard.discountStatistics'), href: '#', current: false },
    // { id: 9, name: t('statsDashboard.fileUploadStatistics'), href: '#', current: false },
    // { id: 10, name: t('statsDashboard.fraudUser'), href: '#', current: false },
    // { id: 11, name: t('statsDashboard.reviewStatistics'), href: '#', current: false },
    // { id: 12, name: t('statsDashboard.notificationActivityLog'), href: '#', current: false },
  ]

  const setCurrentTab = currentTab => setParams({ t: parseInt(currentTab, 10) })

  const mapBranchStatsFiltersToQuery = filters => {
    const query = {
      companyId: filters.companyId || undefined,
      lang: filters.lang || undefined,
      type: filters.type || undefined,
    }
    return query
  }

  const fetchUserStats = async (filters = branchStatsFilters, rangeOverride = null) => {
    try {
      const { startDate, endDate, period } = getQueryDateParams(rangeOverride)
      const response = await apiGet('stats/users/overview', {
        ...mapBranchStatsFiltersToQuery(filters),
        startDate,
        endDate,
        period,
      })
      if (response.success) {
        setStats(response.data)
      }
    } catch (error) {
      console.error('Error fetching user stats:', error)
    }
  }

  const fetchGrowthData = async (rangeOverride = null) => {
    try {
      const { startDate, endDate, period } = getQueryDateParams(rangeOverride)
      const response = await apiGet('stats/users/growth', { period: period || 'monthly', startDate, endDate })
      if (response.success) {
        setGrowthData(response.data.growthData || [])
      }
    } catch (error) {
      console.error('Error fetching growth data:', error)
    }
  }

  const fetchActivityData = async (filters = branchStatsFilters) => {
    try {
      const response = await apiGet('stats/users/activity', {
        companyId: filters.companyId || undefined,
        lang: filters.lang || undefined,
        type: filters.type || undefined,
      })
      if (response.success) {
        setActivityData(response.data)
      }
    } catch (error) {
      console.error('Error fetching activity data:', error)
    }
  }

  const mapCompanyFiltersToQuery = filters => {
    const query = {
      companyId: filters.companyId || undefined,
      governorate: filters.governorate || undefined,
    }
    return query
  }

  const fetchCompanyStats = async (filters = companyFilters, rangeOverride = null) => {
    try {
      const { startDate, endDate, period } = getQueryDateParams(rangeOverride)
      const response = await apiGet('stats/companies/overview', {
        ...mapCompanyFiltersToQuery(filters),
        startDate,
        endDate,
        period,
      })
      if (response.success) {
        setCompanyStats(response.data)
      }
    } catch (error) {
      console.error('Error fetching company stats:', error)
    }
  }

  const mapCarFiltersToQuery = filters => {
    const query = {
      companyId: filters.companyId || undefined,
      branchId: filters.branchId || undefined,
      marketSegment: filters.marketSegment || undefined,
      status: filters.status || undefined,
      insurance: filters.insurance || undefined,
      fuelPolicy: filters.fuelPolicy || undefined,
    }
    if (filters.startDate) query.startDate = filters.startDate
    if (filters.endDate) query.endDate = filters.endDate
    return query
  }

  const fetchCarStats = async (filters = carFilters, rangeOverride = null) => {
    try {
      const global = getQueryDateParams(rangeOverride)
      const startDate = filters.startDate || global.startDate
      const endDate = filters.endDate || global.endDate
      const params = mapCarFiltersToQuery({ ...filters, startDate, endDate })
      const [inventoryResponse, performanceResponse] = await Promise.all([apiGet('stats/cars/inventory', params), apiGet('stats/cars/performance', params)])

      if (inventoryResponse.success) {
        setCarStats(inventoryResponse.data)
      }

      if (performanceResponse.success) {
        setCarPerformanceStats(performanceResponse.data)
      }
    } catch (error) {
      console.error('Error fetching car stats:', error)
    }
  }

  const mapBookingFiltersToQuery = filters => {
    const query = {
      companyId: filters.companyIds?.length === 1 ? filters.companyIds[0] : undefined,
      companyIds: filters.companyIds?.length ? filters.companyIds : undefined,
      instantBooking: filters.instantBooking || undefined,
      companyGovernorates: filters.companyGovernorates?.length ? filters.companyGovernorates : undefined,
      companyMinAge: filters.companyMinAge || undefined,
      companyMaxAge: filters.companyMaxAge || undefined,
      blockedCountries: filters.blockedCountries?.length ? filters.blockedCountries : undefined,
      ajarleePercentageMin: filters.ajarleePercentageMin || undefined,
      ajarleePercentageMax: filters.ajarleePercentageMax || undefined,
      companyDepositMin: filters.companyDepositMin || undefined,
      companyDepositMax: filters.companyDepositMax || undefined,
      vatTax: filters.vatTax || undefined,
      delivery: filters.delivery || undefined,
      deliveryFeeMin: filters.deliveryFeeMin || undefined,
      deliveryFeeMax: filters.deliveryFeeMax || undefined,
      source: filters.source || undefined,
      platform: filters.platform || undefined,
      status: filters.bookingStatus || undefined,
      paymentStatus: filters.paymentStatus || undefined,
    }

    return query
  }

  const normalizeListResponse = data => {
    if (isArray(data)) return data
    if (isArray(data?.company)) return data.company
    if (isArray(data?.branches)) return data.branches
    if (isArray(data?.result)) return data.result
    if (isArray(data?.results)) return data.results
    if (isArray(data?.docs)) return data.docs
    return []
  }

  const fetchBookingStats = async (filters = bookingFilters, rangeOverride = null) => {
    try {
      const { startDate, endDate, period } = getQueryDateParams(rangeOverride)
      const bookingQuery = mapBookingFiltersToQuery(filters)
      if (startDate && endDate) {
        bookingQuery.startDate = startDate
        bookingQuery.endDate = endDate
      }

      const chartPeriod = period || 'monthly'
      const trendsQuery = { ...bookingQuery, period: chartPeriod }
      const performanceQuery = { ...bookingQuery, period: chartPeriod }
      const [overviewResponse, trendsResponse, performanceResponse] = await Promise.all([
        apiGet('stats/bookings/overview', bookingQuery),
        apiGet('stats/bookings/trends', trendsQuery),
        apiGet('stats/bookings/performance', performanceQuery),
      ])

      if (overviewResponse.success) {
        setBookingStats(overviewResponse.data)
      }

      if (trendsResponse.success) {
        setBookingTrends(trendsResponse.data)
      }

      if (performanceResponse.success) {
        setBookingPerformance(performanceResponse.data)
      }
    } catch (error) {
      console.error('Error fetching booking stats:', error)
    }
  }

  const fetchTransactionStats = async (rangeOverride = null) => {
    try {
      const { startDate, endDate } = getQueryDateParams(rangeOverride)
      const response = await apiGet('stats/financial/transactions', { startDate, endDate })
      if (response.success) {
        setTransactionStats(response.data)
      }
    } catch (error) {
      console.error('Error fetching transaction stats:', error)
    }
  }

  const fetchRevenueStats = async (rangeOverride = null) => {
    try {
      const { startDate, endDate, period } = getQueryDateParams(rangeOverride)
      const response = await apiGet('stats/financial/revenue', { period, startDate, endDate })
      if (response.success) {
        setRevenueStats(response.data)
      }
    } catch (error) {
      console.error('Error fetching revenue stats:', error)
    }
  }

  const fetchDeliveryStats = async (rangeOverride = null) => {
    try {
      const { startDate, endDate } = getQueryDateParams(rangeOverride)
      const response = await apiGet('stats/deliveries/overview', { startDate, endDate })
      if (response.success) {
        setDeliveryStats(response.data)
      }
    } catch (error) {
      console.error('Error fetching delivery stats:', error)
    }
  }

  const fetchDiscountStats = async (rangeOverride = null) => {
    try {
      const { startDate, endDate } = getQueryDateParams(rangeOverride)
      const response = await apiGet('stats/discounts/overview', { startDate, endDate })
      if (response.success) {
        setDiscountStats(response.data)
      }
    } catch (error) {
      console.error('Error fetching discount stats:', error)
    }
  }

  const fetchFileUploadStats = async (rangeOverride = null) => {
    try {
      const { startDate, endDate } = getQueryDateParams(rangeOverride)
      const response = await apiGet('stats/files/overview', { startDate, endDate })
      if (response.success) {
        setFileUploadStats(response.data)
      }
    } catch (error) {
      console.error('Error fetching file upload stats:', error)
    }
  }

  const fetchFraudStats = async (rangeOverride = null) => {
    try {
      const { startDate, endDate } = getQueryDateParams(rangeOverride)
      const response = await apiGet('stats/security/fraud', { startDate, endDate })
      if (response.success) {
        setFraudStats(response.data)
      }
    } catch (error) {
      console.error('Error fetching fraud stats:', error)
    }
  }

  const fetchReviewStats = async (rangeOverride = null) => {
    try {
      const { startDate, endDate } = getQueryDateParams(rangeOverride)
      const response = await apiGet('stats/reviews/overview', { startDate, endDate })
      if (response.success) {
        setReviewStats(response.data)
      }
    } catch (error) {
      console.error('Error fetching review stats:', error)
    }
  }

  const fetchNotificationStats = async (rangeOverride = null) => {
    try {
      const { startDate, endDate } = getQueryDateParams(rangeOverride)
      const response = await apiGet('stats/notifications/overview', { startDate, endDate })
      if (response.success) {
        setNotificationStats(response.data)
      }
    } catch (error) {
      console.error('Error fetching notification stats:', error)
    }
  }

  const fetchActivityLogStats = async (rangeOverride = null) => {
    try {
      const { startDate, endDate } = getQueryDateParams(rangeOverride)
      const response = await apiGet('stats/activity-logs/overview', { startDate, endDate })
      if (response.success) {
        setActivityLogStats(response.data)
      }
    } catch (error) {
      console.error('Error fetching activity log stats:', error)
    }
  }

  const fetchActiveSessionStats = async (rangeOverride = null) => {
    try {
      const { startDate, endDate } = getQueryDateParams(rangeOverride)
      const response = await apiGet('stats/sessions/active', { startDate, endDate })
      if (response.success) {
        setActiveSessionStats(response.data)
      }
    } catch (error) {
      console.error('Error fetching active session stats:', error)
    }
  }

  const fetchDevicePlatformStats = async (rangeOverride = null) => {
    try {
      const { startDate, endDate } = getQueryDateParams(rangeOverride)
      const response = await apiGet('stats/sessions/devices', { startDate, endDate })
      if (response.success) {
        setDevicePlatformStats(response.data)
      }
    } catch (error) {
      console.error('Error fetching device and platform stats:', error)
    }
  }

  const fetchCompanies = async () => {
    try {
      const response = await apiGet('companies', { pageSize: 1000 })
      if (response.success) {
        setCompanies(normalizeListResponse(response.data))
      }
    } catch (error) {
      console.error('Error fetching companies:', error)
    }
  }

  const fetchMarketSegments = async () => {
    try {
      const response = await apiGet('market-segments', { pageSize: 500, pageNumber: 1 })
      const list =
        response?.data?.marketSegment?.map(item => ({
          id: item.marketSegment,
          name: isArabic ? item.marketSegmentAr || item.marketSegment : item.marketSegment,
        })) || []
      setMarketSegments(list)
    } catch (error) {
      console.error('Error fetching market segments:', error)
      setMarketSegments([])
    }
  }

  const fetchInsurances = async () => {
    try {
      const response = await apiGet('insurances', { pageSize: 500, pageNumber: 1 })
      const list =
        response?.data?.insurance?.map(item => ({
          id: item.insurance,
          name: isArabic ? item.insuranceAr || item.insurance : item.insurance,
        })) || []
      setInsuranceOptions(list)
    } catch (error) {
      console.error('Error fetching insurances:', error)
      setInsuranceOptions([])
    }
  }

  const fetchFuelPolicies = async () => {
    try {
      const response = await apiGet('fuel-policies', { pageSize: 500, pageNumber: 1 })
      const list =
        response?.data?.fuelPolicy?.map(item => ({
          id: item.fuelPolicy,
          name: isArabic ? item.fuelPolicyAr || item.fuelPolicy : item.fuelPolicy,
        })) || []
      setFuelPolicyOptions(list)
    } catch (error) {
      console.error('Error fetching fuel policies:', error)
      setFuelPolicyOptions([])
    }
  }

  const fetchBranches = async companyId => {
    try {
      if (!companyId) {
        setBranches([])
        return
      }

      const response = await apiGet(`company/${companyId}/branches`, { pageSize: 1000 })
      if (response.success) {
        setBranches(normalizeListResponse(response.data))
      }
    } catch (error) {
      console.error('Error fetching branches:', error)
    }
  }

  const fetchDashboardOverview = async (rangeOverride = null) => {
    try {
      const { startDate, endDate, period } = getQueryDateParams(rangeOverride)
      const response = await apiGet('stats/dashboard/overview', { startDate, endDate, period })
      if (response.success) {
        setDashboardOverview(response.data)
      }
    } catch (error) {
      console.error('Error fetching dashboard overview:', error)
    }
  }

  const fetchOperationFunnelStats = async (rangeOverride = null) => {
    try {
      setOperationFunnelLoading(true)
      const { startDate, endDate } = getQueryDateParams(rangeOverride)
      const response = await apiGet('stats/operations/funnel', { startDate, endDate })
      if (response.success) {
        setOperationFunnelData(response.data)
      }
    } catch (error) {
      console.error('Error fetching operation funnel stats:', error)
    } finally {
      setOperationFunnelLoading(false)
    }
  }

  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true)
      await Promise.all([
        fetchUserStats(branchStatsFilters),
        fetchGrowthData(),
        fetchActivityData(branchStatsFilters),
        fetchCarStats(carFilters),
        fetchTransactionStats(),
        fetchRevenueStats(),
        fetchDeliveryStats(),
        fetchDiscountStats(),
        fetchFileUploadStats(),
        fetchFraudStats(),
        fetchReviewStats(),
        fetchNotificationStats(),
        fetchActivityLogStats(),
        fetchActiveSessionStats(),
        fetchDevicePlatformStats(),
        fetchDashboardOverview(),
        fetchCompanies(),
      ])
      setLoading(false)
    }

    loadAllData()
  }, [])

  useEffect(() => {
    fetchMarketSegments()
    fetchInsurances()
    fetchFuelPolicies()
  }, [isArabic])

  useEffect(() => {
    fetchBookingStats(bookingFilters)
  }, [bookingFilters])

  useEffect(() => {
    fetchCompanyStats(companyFilters)
  }, [companyFilters])

  useEffect(() => {
    fetchCarStats(carFilters)
  }, [carFilters])

  useEffect(() => {
    fetchUserStats(branchStatsFilters)
    fetchActivityData(branchStatsFilters)
  }, [branchStatsFilters])

  useEffect(() => {
    const singleCompanyId = bookingFilters.companyIds?.length === 1 ? bookingFilters.companyIds[0] : null
    fetchBranches(singleCompanyId)
  }, [bookingFilters.companyIds])

  useEffect(() => {
    if (params?.t === 13) {
      fetchOperationFunnelStats()
    }
  }, [params?.t])

  const handleGlobalDateRangeChange = ranges => {
    const selection = ranges?.selection
    if (!selection?.startDate || !selection?.endDate) return

    const start = moment(selection.startDate).startOf('day')
    const end = moment(selection.endDate).endOf('day')
    const range = { startDate: start, endDate: end }

    setGlobalDateSelection({
      startDate: selection.startDate,
      endDate: selection.endDate,
      key: 'selection',
    })
    setGlobalDateRange(range)
    applyGlobalDateRange(range)
  }

  const applyGlobalDateRange = async (rangeOverride = null) => {
    const range = rangeOverride || globalDateRange
    const requests = [
      fetchGrowthData(range),
      fetchRevenueStats(range),
      fetchUserStats(branchStatsFilters, range),
      fetchCompanyStats(companyFilters, range),
      fetchBookingStats(bookingFilters, range),
      fetchCarStats(carFilters, range),
      fetchTransactionStats(range),
      fetchDeliveryStats(range),
      fetchDiscountStats(range),
      fetchFileUploadStats(range),
      fetchFraudStats(range),
      fetchReviewStats(range),
      fetchNotificationStats(range),
      fetchActivityLogStats(range),
      fetchActiveSessionStats(range),
      fetchDevicePlatformStats(range),
      fetchDashboardOverview(range),
    ]
    if (params?.t === 13) {
      requests.push(fetchOperationFunnelStats(range))
    }
    await Promise.all(requests)
  }

  const clearGlobalDateRange = () => {
    const range = { startDate: null, endDate: null }
    setGlobalDateRange(range)
    setGlobalDateSelection({
      startDate: new Date(),
      endDate: new Date(),
      key: 'selection',
    })
    applyGlobalDateRange(range)
  }

  const handleBookingFilterChange = (key, value) => {
    setBookingFilters(prev => {
      const nextFilters = { ...prev, [key]: value }
      if (key === 'companyIds') {
        nextFilters.branchId = ''
      }
      return nextFilters
    })
  }

  const clearBookingFilters = () => {
    setBookingFilters({
      companyIds: [],
      instantBooking: '',
      companyGovernorates: [],
      companyMinAge: '',
      companyMaxAge: '',
      blockedCountries: [],
      ajarleePercentageMin: '',
      ajarleePercentageMax: '',
      companyDepositMin: '',
      companyDepositMax: '',
      vatTax: '',
      delivery: '',
      deliveryFeeMin: '',
      deliveryFeeMax: '',
      source: '',
      platform: '',
      bookingStatus: '',
      paymentStatus: '',
    })
  }

  const selectedBookingStatusLabel = bookingFilters.bookingStatus
    ? t(`statsDashboard.bookingStatus.${bookingFilters.bookingStatus}`, { defaultValue: bookingFilters.bookingStatus })
    : ''
  const selectedPaymentStatusLabel = bookingFilters.paymentStatus
    ? t(`statsDashboard.paymentStatus.${bookingFilters.paymentStatus}`, { defaultValue: bookingFilters.paymentStatus })
    : ''
  const bookingStatusScope = [
    bookingFilters.bookingStatus ? t('statsDashboard.descriptions.statusScopeSelected', { status: selectedBookingStatusLabel }) : t('statsDashboard.descriptions.statusScopeAll'),
    bookingFilters.paymentStatus ? t('statsDashboard.descriptions.paymentScopeSelected', { status: selectedPaymentStatusLabel }) : t('statsDashboard.descriptions.paymentScopeAll'),
  ].join(' ')

  const handleCompanyFilterChange = (key, value) => {
    setCompanyFilters(prev => ({ ...prev, [key]: value }))
  }

  const clearCompanyFilters = () => {
    setCompanyFilters({
      companyId: '',
      governorate: '',
    })
  }

  const handleCarFilterChange = (key, value) => {
    setCarFilters(prev => {
      const next = { ...prev, [key]: value }
      if (key === 'companyId') {
        next.branchId = ''
      }
      return next
    })
    if (key === 'companyId') {
      fetchBranches(value || null)
    }
  }

  const clearCarFilters = () => {
    setCarFilters({
      startDate: '',
      endDate: '',
      companyId: '',
      branchId: '',
      marketSegment: '',
      status: '',
      insurance: '',
      fuelPolicy: '',
    })
    setCarDateRange({ startDate: null, endDate: null })
  }

  const handleCarDateRangeChange = ranges => {
    const start = ranges?.selection?.startDate ? moment(ranges.selection.startDate) : null
    const end = ranges?.selection?.endDate ? moment(ranges.selection.endDate) : null
    setCarDateRange({ startDate: start, endDate: end })
    handleCarFilterChange('startDate', start ? start.format('YYYY-MM-DD') : '')
    handleCarFilterChange('endDate', end ? end.format('YYYY-MM-DD') : '')
  }

  const handleBranchStatsFilterChange = (key, value) => {
    setBranchStatsFilters(prev => ({ ...prev, [key]: value }))
  }

  const clearBranchStatsFilters = () => {
    setBranchStatsFilters({
      companyId: '',
      lang: '',
      type: '',
    })
  }

  const percentGrowthLabel = value => {
    if (value == null || value === '') return null
    const num = Number(value)
    const prefix = num > 0 ? '+' : ''
    return t('statsDashboard.common.percentGrowth', { value: `${prefix}${num}` })
  }

  const growthTrend = value => (Number(value) > 0 ? 'up' : 'down')

  const percentOfTotal = value => t('statsDashboard.common.percentOfTotal', { value })
  const changeInPeriod = (count, withPlus = false) => {
    const period = getQueryDateParams().period
    const periodKey = period === 'daily' ? 'Day' : period === 'yearly' ? 'Year' : 'Month'
    const key = withPlus ? `statsDashboard.common.changeThis${periodKey}Plus` : `statsDashboard.common.changeThis${periodKey}`
    return t(key, { count })
  }
  const changeThisMonth = (count, withPlus = false) => changeInPeriod(count, withPlus)
  const changeThisWeek = count => changeInPeriod(count, false)
  const percentOfAvailable = value => t('statsDashboard.common.percentOfAvailable', { value })
  const pctOfTotalFrom = (part, whole) => (whole ? percentOfTotal(((part / whole) * 100).toFixed(1)) : null)

  const StatCard = ({ title, value, change, icon, trend = 'up', description }) => (
    <div className="bg-white overflow-hidden shadow rounded-lg h-full">
      <div className="p-4 sm:p-5">
        <div className="flex items-start">
          <div className="flex-shrink-0 mt-0.5">{icon}</div>
          <div className="ml-4 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
              <dd className="flex items-baseline">
                <div className="text-2xl font-semibold text-gray-900">{value}</div>
                {change && (
                  <div className={`ml-2 flex items-baseline text-sm font-semibold ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                    <svg
                      className={`self-center flex-shrink-0 h-4 w-4 ${trend === 'up' ? 'text-green-500' : 'text-red-500'}`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="sr-only">{trend === 'up' ? t('statsDashboard.common.increased') : t('statsDashboard.common.decreased')}</span>
                    {change}
                  </div>
                )}
              </dd>
              {description && <p className="mt-1.5 text-xs leading-4 text-gray-400">{description}</p>}
            </dl>
          </div>
        </div>
      </div>
    </div>
  )

  const getTooltipDataKeyLabel = dataKey => {
    if (dataKey === 'count') return t('statsDashboard.labels.count')
    if (dataKey === 'revenue') return t('statsDashboard.tables.revenue')
    if (dataKey === 'value') return t('statsDashboard.labels.count')
    if (dataKey === 'activeUsers') return t('statsDashboard.operationFunnel.activeUsers')
    if (dataKey === 'newUsers') return t('statsDashboard.operationFunnel.newUsers')
    if (dataKey === 'carViews') return t('statsDashboard.operationFunnel.carViews')
    if (dataKey === 'totalBookings') return t('statsDashboard.operationFunnel.totalBookings')
    if (dataKey === 'paidBookings') return t('statsDashboard.operationFunnel.paidBookings')
    if (dataKey === 'instantBookings') return t('statsDashboard.operationFunnel.instantBookings')
    if (dataKey === 'nonInstantBookings') return t('statsDashboard.operationFunnel.nonInstantBookings')
    return dataKey
  }

  const formatFunnelMetric = value => {
    if (value == null || Number.isNaN(Number(value))) return t('statsDashboard.common.notAvailable')
    return Number(value).toLocaleString()
  }

  const operationFunnelSummary = operationFunnelData?.summary
  const operationFunnelStages = useMemo(() => {
    const summary = operationFunnelData?.summary
    if (!summary) return []

    const carViews = Number(summary.carViews || 0)
    const totalBookings = Number(summary.totalBookings || 0)
    const paidBookings = Number(summary.paidBookings || 0)

    const formatStageCount = (count, conversionPct = null, ofLabel = null) => {
      const base = Number(count || 0).toLocaleString()
      if (conversionPct == null || Number.isNaN(conversionPct) || !ofLabel) return base
      return t('statsDashboard.operationFunnel.conversionLabel', {
        count: base,
        rate: conversionPct.toFixed(1),
        of: ofLabel,
      })
    }

    const bookingsFromPreOrders = carViews > 0 ? (totalBookings / carViews) * 100 : null
    const paidFromBookings = totalBookings > 0 ? (paidBookings / totalBookings) * 100 : null
    const preOrdersLabel = t('statsDashboard.operationFunnel.carViews')
    const preOrdersShort = t('statsDashboard.operationFunnel.preOrdersShort')
    const totalBookingsLabel = t('statsDashboard.operationFunnel.totalBookings')

    return [
      {
        key: 'activeUsers',
        label: t('statsDashboard.operationFunnel.activeUsers'),
        count: Number(summary.activeUsers || 0),
        displayCount: formatStageCount(summary.activeUsers),
      },
      {
        key: 'newUsers',
        label: t('statsDashboard.operationFunnel.newUsers'),
        count: Number(summary.newUsers || 0),
        displayCount: formatStageCount(summary.newUsers),
      },
      {
        key: 'carViews',
        label: preOrdersLabel,
        count: carViews,
        displayCount: formatStageCount(carViews),
      },
      {
        key: 'totalBookings',
        label: totalBookingsLabel,
        count: totalBookings,
        displayCount: formatStageCount(totalBookings, bookingsFromPreOrders, preOrdersShort),
      },
      {
        key: 'paidBookings',
        label: t('statsDashboard.operationFunnel.paidBookings'),
        count: paidBookings,
        displayCount: formatStageCount(paidBookings, paidFromBookings, totalBookingsLabel),
      },
    ]
  }, [operationFunnelData, t])

  const renderFunnelStageLabel = props => {
    const { x, y, width, height, value } = props
    if (value == null || value === '') return null
    const labelX = Number(x || 0) + Number(width || 0) + 8
    const labelY = Number(y || 0) + Number(height || 0) / 2
    return (
      <text x={labelX} y={labelY} fill="#374151" fontSize={12} textAnchor="start" dominantBaseline="middle">
        {String(value)}
      </text>
    )
  }

  const operationFunnelDaily = useMemo(() => operationFunnelData?.daily || [], [operationFunnelData])

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {`${getTooltipDataKeyLabel(entry.dataKey)}: ${entry.value}`}
            </p>
          ))}
        </div>
      )
    }
    return null
  }

  const getUserTypesData = () => {
    if (!stats?.usersByType) return []
    return Object.entries(stats.usersByType).map(([type, count]) => ({
      type: t(`statsDashboard.userTypes.${type}`, { defaultValue: type }),
      count,
    }))
  }

  const getDeviceData = () => {
    if (!activityData?.usersByDeviceType) return []
    return [
      { name: t('statsDashboard.platforms.android'), value: activityData.usersByDeviceType.android || 0, fill: '#34d399' },
      { name: t('statsDashboard.platforms.ios'), value: activityData.usersByDeviceType.ios || 0, fill: '#60a5fa' },
      { name: t('statsDashboard.platforms.browser'), value: activityData.usersByDeviceType.web || 0, fill: '#fbbf24' },
    ]
  }

  const getNationalityData = () => {
    if (!stats?.usersByNationality) return []
    return stats.usersByNationality.slice(0, 5).map(item => ({
      category: item.nationality || t('statsDashboard.common.unknown'),
      count: item.count,
      color: '#8884d8',
    }))
  }

  const getCompanyData = () => {
    if (!stats?.usersByCompany) return []
    return stats.usersByCompany.slice(0, 10).map(item => ({
      company: item.companyName || t('statsDashboard.common.unknown'),
      users: item.userCount,
      active: item.activeUsers,
      verified: item.verifiedUsers,
    }))
  }

  const getBranchData = () => {
    if (!stats?.usersByBranch) return []
    return stats.usersByBranch.slice(0, 10).map(item => ({
      branch: item.branchName || t('statsDashboard.common.unknown'),
      users: item.userCount,
      active: item.activeUsers,
    }))
  }

  const getAgeGroupData = () => {
    if (!stats?.usersByAgeGroup) return []
    return Object.entries(stats.usersByAgeGroup).map(([ageGroup, count]) => ({
      ageGroup,
      count,
    }))
  }

  const getLanguageData = () => {
    if (!stats?.usersByLanguage) return []
    return Object.entries(stats.usersByLanguage).map(([language, count]) => ({
      language: language || t('statsDashboard.common.unknown'),
      count,
    }))
  }

  const getResidencyData = () => {
    if (!stats?.usersByResidency) return []
    return [
      { name: t('statsDashboard.residency.resident'), value: stats.usersByResidency.resident || 0, fill: '#34d399' },
      { name: t('statsDashboard.residency.nonResident'), value: stats.usersByResidency.nonResident || 0, fill: '#fbbf24' },
    ]
  }

  const calculateGrowthRate = (current, previous) => {
    if (!previous || previous === 0) return '+100%'
    const rate = ((current - previous) / previous) * 100
    return `${rate >= 0 ? '+' : ''}${rate.toFixed(1)}%`
  }

  const getAverageUtilizationRate = () => {
    if (!companyStats?.companiesByCarCount?.length) return '0%'
    const totalUtilization = companyStats.companiesByCarCount.reduce((sum, company) => sum + (company.utilizationRate || 0), 0)
    return `${(totalUtilization / companyStats.companiesByCarCount.length).toFixed(1)}%`
  }

  const getAverageAcceptanceRate = () => {
    if (!companyStats?.companiesByPerformance?.length) return '0%'
    const totalAcceptance = companyStats.companiesByPerformance.reduce((sum, company) => sum + (company.acceptanceRatio || 0), 0)
    return `${(totalAcceptance / companyStats.companiesByPerformance.length).toFixed(1)}%`
  }

  const getCompanyGovernorateData = () => {
    if (!companyStats?.companiesByGovernorate) return []
    return Object.entries(companyStats.companiesByGovernorate).map(([governorate, count]) => ({
      governorate,
      count,
    }))
  }

  const getCarFuelData = () => {
    if (!carStats?.carsByFuelType) return []
    return Object.entries(carStats.carsByFuelType).map(([fuelType, count]) => ({
      name: fuelType || t('statsDashboard.common.unknown'),
      value: count,
      fill: fuelType === 'Petrol' ? '#10b981' : fuelType === 'Diesel' ? '#3b82f6' : '#8b5cf6',
    }))
  }

  const getCarTransmissionData = () => {
    if (!carStats?.carsByTransmission) return []
    return Object.entries(carStats.carsByTransmission).map(([transmission, count]) => ({
      name: t(`statsDashboard.transmission.${transmission}`, { defaultValue: transmission || t('statsDashboard.common.unknown') }),
      value: count,
      fill: transmission === 'Automatic' ? '#10b981' : '#3b82f6',
    }))
  }

  const getCarAgeData = () => {
    if (!carStats?.carsByAge) return []
    return Object.entries(carStats.carsByAge).map(([ageGroup, count]) => ({
      name: ageGroup,
      value: count,
      fill: '#8884d8',
    }))
  }

  const getCarConditionData = () => {
    if (!carStats?.carsByCondition) return []
    return Object.entries(carStats.carsByCondition).map(([condition, count]) => ({
      name: t(`statsDashboard.condition.${condition}`, { defaultValue: condition }),
      value: count,
      fill: condition === 'Excellent' ? '#10b981' : condition === 'Good' ? '#3b82f6' : condition === 'Fair' ? '#f59e0b' : '#ef4444',
    }))
  }

  const getCarUtilizationData = () => {
    if (!carPerformanceStats?.carsByUtilization) return []
    return Object.entries(carPerformanceStats.carsByUtilization).map(([level, count]) => ({
      name: t(`statsDashboard.utilization.${level}`, { defaultValue: level }),
      value: count,
      fill: level === 'high' ? '#10b981' : level === 'medium' ? '#f59e0b' : '#ef4444',
    }))
  }

  const getCarMaintenanceData = () => {
    if (!carPerformanceStats?.carsByMaintenance) return []
    return Object.entries(carPerformanceStats.carsByMaintenance).map(([status, count]) => ({
      name: t(`statsDashboard.maintenance.${status}`, { defaultValue: status }),
      value: count,
      fill: status === 'underMaintenance' ? '#ef4444' : status === 'dueForService' ? '#f59e0b' : '#10b981',
    }))
  }

  const getCarMarketSegmentData = () => {
    if (!carStats?.carsByMarketSegment) return []
    return Object.entries(carStats.carsByMarketSegment).map(([segment, count]) => ({
      name: segment || t('statsDashboard.common.unknown'),
      value: count,
      fill: '#8884d8',
    }))
  }

  const getBookingStatusData = () => {
    if (!bookingStats?.bookingsByStatus) return []
    const hiddenStatuses = ['void', 'deposit', 'reserved', 'paid']
    return Object.entries(bookingStats.bookingsByStatus)
      .filter(([status, count]) => !includes(hiddenStatuses, status) && Number(count) > 0)
      .map(([status, count]) => ({
        name: t(`statsDashboard.bookingStatus.${status}`, { defaultValue: status }),
        value: count,
        fill:
          status === 'pending'
            ? '#fbbf24'
            : status === 'confirmed'
            ? '#3b82f6'
            : status === 'completed'
            ? '#10b981'
            : status === 'cancelled'
            ? '#ef4444'
            : status === 'rejected'
            ? '#dc2626'
            : status === 'inProgress'
            ? '#8b5cf6'
            : status === 'underReview'
            ? '#0ea5e9'
            : status === 'failedPayment'
            ? '#f97316'
            : status === 'waiting_for_payment_approval'
            ? '#14b8a6'
            : '#6b7280',
      }))
  }

  const bookingSourceLabel = source => {
    const labels = {
      website: t('statsDashboard.sources.website'),
      mobile_app: t('statsDashboard.sources.mobileApp'),
      admin_panel: t('statsDashboard.sources.adminPanel'),
      company_dashboard: t('statsDashboard.sources.companyDashboard'),
      other: t('statsDashboard.common.other'),
      unknown: t('statsDashboard.common.unknown'),
    }
    return labels[source] || source
  }

  const bookingSourceFill = source => {
    const fills = {
      website: '#3b82f6',
      mobile_app: '#10b981',
      admin_panel: '#8b5cf6',
      company_dashboard: '#f59e0b',
      other: '#6b7280',
      unknown: '#94a3b8',
    }
    return fills[source] || '#8b5cf6'
  }

  const bookingPlatformLabel = platform => {
    const labels = {
      android: t('statsDashboard.platforms.android'),
      ios: t('statsDashboard.platforms.ios'),
      web: t('statsDashboard.platforms.browser'),
      other: t('statsDashboard.common.other'),
      unknown: t('statsDashboard.common.unknown'),
    }
    return labels[platform] || platform
  }

  const bookingPlatformFill = platform => {
    const fills = {
      android: '#10b981',
      ios: '#3b82f6',
      web: '#f59e0b',
      other: '#6b7280',
      unknown: '#94a3b8',
    }
    return fills[platform] || '#8b5cf6'
  }

  const getBookingSourceData = (includeZeros = false) => {
    if (!bookingStats?.bookingsBySource) return []
    return Object.entries(bookingStats.bookingsBySource)
      .map(([source, count]) => ({
        name: bookingSourceLabel(source),
        value: count,
        fill: bookingSourceFill(source),
        key: source,
      }))
      .filter(entry => includeZeros || entry.value > 0)
  }

  const getBookingPlatformData = (includeZeros = false) => {
    if (!bookingStats?.bookingsByPlatform) return []
    return Object.entries(bookingStats.bookingsByPlatform)
      .map(([platform, count]) => ({
        name: bookingPlatformLabel(platform),
        value: count,
        fill: bookingPlatformFill(platform),
        key: platform,
      }))
      .filter(entry => includeZeros || entry.value > 0)
  }

  const bookingSourceFilterOptions = [
    { id: '', name: t('statsDashboard.filters.allSources') },
    { id: 'website', name: t('statsDashboard.sources.website') },
    { id: 'mobile_app', name: t('statsDashboard.sources.mobileApp') },
    { id: 'admin_panel', name: t('statsDashboard.sources.adminPanel') },
    { id: 'company_dashboard', name: t('statsDashboard.sources.companyDashboard') },
  ]

  const bookingPlatformFilterOptions = [
    { id: '', name: t('statsDashboard.filters.allPlatforms') },
    { id: 'web', name: t('statsDashboard.platforms.browser') },
    { id: 'ios', name: t('statsDashboard.platforms.ios') },
    { id: 'android', name: t('statsDashboard.platforms.android') },
  ]

  const bookingStatusFilterOptions = [
    { id: '', name: t('statsDashboard.filters.allStatuses') },
    { id: 'pending', name: t('statsDashboard.bookingStatus.pending') },
    { id: 'confirmed', name: t('statsDashboard.bookingStatus.confirmed') },
    { id: 'inProgress', name: t('statsDashboard.bookingStatus.inProgress') },
    { id: 'completed', name: t('statsDashboard.bookingStatus.completed') },
    { id: 'cancelled', name: t('statsDashboard.bookingStatus.cancelled') },
    { id: 'rejected', name: t('statsDashboard.bookingStatus.rejected') },
    { id: 'underReview', name: t('statsDashboard.bookingStatus.underReview') },
    { id: 'failedPayment', name: t('statsDashboard.bookingStatus.failedPayment') },
    { id: 'waiting_for_payment_approval', name: t('statsDashboard.bookingStatus.waiting_for_payment_approval') },
  ]

  const bookingPaymentStatusFilterOptions = [
    { id: '', name: t('statsDashboard.filters.allPaymentStatuses') },
    { id: 'unpaid', name: t('statsDashboard.paymentStatus.unpaid') },
    { id: 'paid', name: t('statsDashboard.paymentStatus.paid') },
    { id: 'partialpaid', name: t('statsDashboard.paymentStatus.partialpaid') },
    { id: 'waiting_for_payment_approval', name: t('statsDashboard.paymentStatus.waiting_for_payment_approval') },
    { id: 'failure', name: t('statsDashboard.paymentStatus.failure') },
    { id: 'refund', name: t('statsDashboard.paymentStatus.refund') },
  ]

  const getBookingDurationData = () => {
    if (!bookingStats?.bookingsByDuration) return []
    return Object.entries(bookingStats.bookingsByDuration).map(([duration, count]) => ({
      name:
        duration === 'daily'
          ? t('statsDashboard.options.daily')
          : duration === 'weekly'
          ? t('statsDashboard.options.weekly')
          : duration === 'monthly'
          ? t('statsDashboard.options.monthly')
          : duration === 'longTerm'
          ? t('statsDashboard.options.longTerm')
          : duration,
      value: count,
      fill: duration === 'daily' ? '#10b981' : duration === 'weekly' ? '#3b82f6' : duration === 'monthly' ? '#f59e0b' : '#8b5cf6',
    }))
  }

  const getRatingDistributionData = () => {
    if (!bookingPerformance?.customerSatisfaction?.ratingDistribution) return []
    return Object.entries(bookingPerformance.customerSatisfaction.ratingDistribution).map(([rating, count]) => ({
      name: t('statsDashboard.labels.starsCount', { count: rating }),
      value: count,
      fill: rating === '5' ? '#10b981' : rating === '4' ? '#3b82f6' : rating === '3' ? '#f59e0b' : rating === '2' ? '#f97316' : '#ef4444',
    }))
  }

  const bookingCompanyOptions = isArray(companies) ? companies : []
  const bookingBranchOptions = isArray(branches) ? branches : []
  const booleanBookingFilterOptions = [
    { id: '', name: t('statsDashboard.filters.all') },
    { id: 'true', name: t('statsDashboard.filters.yes') },
    { id: 'false', name: t('statsDashboard.filters.no') },
  ]
  const companySingleFilterOptions = [
    { id: '', name: t('statsDashboard.filters.allCompanies') },
    ...bookingCompanyOptions.map(company => ({ id: company._id, name: company.name })),
  ]
  const bookingCompanyMultiFilterOptions = bookingCompanyOptions.map(company => ({
    id: company._id,
    name: company.name,
  }))
  const governorateMultiFilterOptions = [...new Set((bookingCompanyOptions || []).map(company => company.governorate).filter(Boolean))].map(governorate => ({
    id: governorate,
    name: governorate,
  }))
  const governorateSingleFilterOptions = [{ id: '', name: t('statsDashboard.filters.allGovernorates') }, ...governorateMultiFilterOptions]
  const blockedCountriesFilterOptions = getLocalizedCountries(i18n.language || 'en').map(country => ({
    id: country.id,
    name: country.name,
  }))
  const carCompanyOptions = [{ id: '', name: t('statsDashboard.filters.allCompanies') }, ...bookingCompanyOptions.map(company => ({ id: company._id, name: company.name }))]
  const carBranchOptions = [{ id: '', name: t('statsDashboard.filters.allBranches') }, ...bookingBranchOptions.map(branch => ({ id: branch._id, name: branch.name }))]
  const marketSegmentFilterOptions = [{ id: '', name: t('statsDashboard.filters.allSegments') }, ...marketSegments]
  const carStatusFilterOptions = [
    { id: '', name: t('statsDashboard.filters.allStatuses') },
    { id: 'active', name: t('statsDashboard.filters.active') },
    { id: 'booked', name: t('statsDashboard.filters.booked') },
    { id: 'out of service', name: t('statsDashboard.cards.outOfService') },
    { id: 'inProgress', name: t('statsDashboard.filters.inProgress') },
  ]
  const insuranceFilterOptions = [{ id: '', name: t('statsDashboard.filters.allInsurances') }, ...insuranceOptions]
  const fuelPolicyFilterOptions = [{ id: '', name: t('statsDashboard.filters.allFuelPolicies') }, ...fuelPolicyOptions]
  const languageFilterOptions = [
    { id: '', name: t('statsDashboard.filters.allLanguages') },
    { id: 'en', name: t('statsDashboard.filters.english') },
    { id: 'ar', name: t('statsDashboard.filters.arabic') },
  ]
  const userTypeFilterOptions = [
    { id: '', name: t('statsDashboard.filters.allTypes') },
    { id: 'user', name: t('statsDashboard.userTypes.user') },
    { id: 'company', name: t('statsDashboard.userTypes.company') },
    { id: 'admin', name: t('statsDashboard.userTypes.admin') },
  ]

  const getTransactionStatusData = () => {
    if (!transactionStats?.transactionsByStatus) return []
    return Object.entries(transactionStats.transactionsByStatus).map(([status, count]) => ({
      name: t(`statsDashboard.transactionStatus.${status}`, { defaultValue: status }),
      value: count,
      fill: status === 'initiated' ? '#fbbf24' : status === 'completed' ? '#10b981' : status === 'canceled' ? '#ef4444' : '#8b5cf6',
    }))
  }

  const getPaymentMethodData = () => {
    if (!transactionStats?.transactionsByPaymentMethod) return []
    const paymentMethodKeys = {
      credit_card: 'creditCard',
      debit_card: 'debitCard',
      bank_transfer: 'bankTransfer',
      cash: 'cash',
    }
    return Object.entries(transactionStats.transactionsByPaymentMethod).map(([method, count]) => ({
      name: t(`statsDashboard.payment.${paymentMethodKeys[method] || method}`, { defaultValue: method }),
      value: count,
      fill: method === 'credit_card' ? '#10b981' : method === 'debit_card' ? '#3b82f6' : method === 'bank_transfer' ? '#8b5cf6' : method === 'cash' ? '#f59e0b' : '#6b7280',
    }))
  }

  const getRevenueByPeriodData = () => {
    if (!revenueStats?.revenueByPeriod) return []
    return revenueStats.revenueByPeriod.map(item => ({
      period: item.period,
      revenue: item.revenue,
      bookingCount: item.bookingCount,
      averageValue: item.averageValue,
    }))
  }

  const getRevenueByCompanyData = () => {
    if (!revenueStats?.revenueByCompany) return []
    return revenueStats.revenueByCompany.slice(0, 10).map(company => ({
      companyName: company.companyName,
      revenue: company.revenue,
      percentage: company.percentage,
      growthRate: company.growthRate,
    }))
  }

  const getRevenueByLocationData = () => {
    if (!revenueStats?.revenueByLocation) return []
    return revenueStats.revenueByLocation.slice(0, 10).map(location => ({
      location: location.location,
      revenue: location.revenue,
      percentage: location.percentage,
    }))
  }

  const getRevenueBySourceData = () => {
    if (!revenueStats?.revenueBySource) return []
    return Object.entries(revenueStats.revenueBySource)
      .map(([source, value]) => ({
        name: bookingSourceLabel(source),
        value: value || 0,
        fill: bookingSourceFill(source),
      }))
      .filter(entry => entry.value > 0)
  }

  const getDeliveryTypeData = () => {
    if (!deliveryStats?.deliveriesByType) return []
    return [
      { name: t('statsDashboard.cards.deliverToMe'), value: deliveryStats.deliveriesByType.DELIVER_TO_ME || 0, fill: '#10b981' },
      { name: t('statsDashboard.cards.pickUpMyself'), value: deliveryStats.deliveriesByType.PICK_UP_MYSELF || 0, fill: '#3b82f6' },
    ]
  }

  const getDeliveryCompanyData = () => {
    if (!deliveryStats?.deliveriesByCompany) return []
    return deliveryStats.deliveriesByCompany.slice(0, 10).map(company => ({
      companyName: company.companyName,
      totalDeliveries: company.totalDeliveries,
      deliverToMe: company.deliverToMe,
      pickupMyself: company.pickupMyself,
      deliveryCost: company.deliveryCost,
    }))
  }

  const getDeliveryLocationData = () => {
    if (!deliveryStats?.deliveriesByLocation) return []
    return deliveryStats.deliveriesByLocation.map(location => ({
      location: location.location || t('statsDashboard.common.unknown'),
      deliveryCount: location.deliveryCount,
      pickupCount: location.pickupCount,
      totalCost: location.totalCost,
    }))
  }

  const getDeliveryTrendsData = () => {
    return []
  }

  const getDiscountTypeData = () => {
    if (!discountStats?.discountsByType) return []
    return Object.entries(discountStats.discountsByType).map(([type, count]) => ({
      name: t(`statsDashboard.discountType.${type}`, { defaultValue: type }),
      value: count,
      fill: type === 'Flat' ? '#10b981' : '#3b82f6',
    }))
  }

  const getDiscountCompanyData = () => {
    if (!discountStats?.discountsByCompany) return []
    return discountStats.discountsByCompany.slice(0, 10).map(company => ({
      companyName: company.companyName || t('statsDashboard.common.unknown'),
      totalDiscounts: company.totalDiscounts,
      activeDiscounts: company.activeDiscounts,
      totalUsage: company.totalUsage,
    }))
  }

  const getDiscountTrendsData = () => {
    if (!discountStats?.discountTrends) return []
    // This would need to be implemented if you want to show monthly trends
    // For now, return empty array since the API doesn't provide monthly data
    return []
  }

  const getFileTypeData = () => {
    if (!fileUploadStats?.filesByType) return []
    return Object.entries(fileUploadStats.filesByType).map(([type, data]) => ({
      name: type || t('statsDashboard.common.unknown'),
      value: data.count,
      fill: type === 'csv' ? '#10b981' : type === 'pdf' ? '#3b82f6' : type === 'image' ? '#f59e0b' : '#8b5cf6',
    }))
  }

  const getStorageByTypeData = () => {
    if (!fileUploadStats?.storageByType) return []
    return Object.entries(fileUploadStats.storageByType).map(([type, size]) => ({
      name: type || t('statsDashboard.common.unknown'),
      value: parseFloat(size.replace(/[^\d.]/g, '')),
      size,
      fill: type === 'csv' ? '#10b981' : type === 'pdf' ? '#3b82f6' : type === 'image' ? '#f59e0b' : '#8b5cf6',
    }))
  }

  const getFileCompanyData = () => {
    if (!fileUploadStats?.filesByCompany) return []
    return fileUploadStats.filesByCompany.slice(0, 10).map(company => ({
      companyName: company.companyName || t('statsDashboard.common.unknown'),
      totalFiles: company.totalFiles,
      storageUsed: company.storageUsed,
      fileTypes: company.fileTypes,
    }))
  }

  const getStorageUtilizationData = () => {
    if (!fileUploadStats?.storageMetrics) return []
    const metrics = fileUploadStats.storageMetrics
    return [
      { name: t('statsDashboard.storage.usedStorage'), value: parseFloat(metrics.utilizationRate), fill: '#ef4444' },
      { name: t('statsDashboard.storage.availableStorage'), value: 100 - parseFloat(metrics.utilizationRate), fill: '#10b981' },
    ]
  }

  const getLargestFilesData = () => {
    if (!fileUploadStats?.storageMetrics?.largestFiles) return []
    return fileUploadStats.storageMetrics.largestFiles.map(file => ({
      fileName: file.fileName || t('statsDashboard.common.unknown'),
      fileSize: file.fileSize,
      fileType: file.fileType,
      companyName: file.companyName || t('statsDashboard.common.unknown'),
      uploadedAt: file.uploadedAt,
    }))
  }

  const getReviewRatingData = () => {
    if (!reviewStats?.ratingDistribution) return []
    return Object.entries(reviewStats.ratingDistribution).map(([rating, count]) => ({
      name: t('statsDashboard.labels.starsCount', { count: rating }),
      value: count,
      fill: rating === '5' ? '#10b981' : rating === '4' ? '#3b82f6' : rating === '3' ? '#f59e0b' : rating === '2' ? '#f97316' : '#ef4444',
    }))
  }

  const getReviewSourceData = () => {
    if (!reviewStats?.reviewsBySource) return []
    return Object.entries(reviewStats.reviewsBySource)
      .map(([source, count]) => ({
        name: bookingSourceLabel(source),
        value: count,
        fill: bookingSourceFill(source),
      }))
      .filter(entry => entry.value > 0)
  }

  const getReviewTrendsData = () => {
    if (!reviewStats?.reviewsByMonth) return []
    return reviewStats.reviewsByMonth.map(item => ({
      month: item.month,
      count: item.count,
      averageRating: item.averageRating,
    }))
  }

  const getNotificationUserTypeData = () => {
    if (!notificationStats?.notificationsByUserType) return []
    return Object.entries(notificationStats.notificationsByUserType).map(([type, count]) => ({
      name: t(`statsDashboard.userTypes.${type}`, { defaultValue: type }),
      value: count,
      fill: type === 'admin' ? '#ef4444' : type === 'company' ? '#3b82f6' : '#10b981',
    }))
  }

  const getNotificationDeliveryData = () => {
    if (!notificationStats?.deliveryStatistics?.deliveryByPlatform) return []
    return Object.entries(notificationStats.deliveryStatistics.deliveryByPlatform).map(([platform, count]) => ({
      name: t(`statsDashboard.notificationPlatform.${platform}`, { defaultValue: platform }),
      value: count,
      fill: platform === 'email' ? '#3b82f6' : platform === 'push' ? '#10b981' : '#f59e0b',
    }))
  }

  const getActivityLogUserTypeData = () => {
    if (!activityLogStats?.logsByUserType) return []
    return Object.entries(activityLogStats.logsByUserType).map(([type, count]) => ({
      name: t(`statsDashboard.userTypes.${type}`, { defaultValue: t('statsDashboard.common.other') }),
      value: count,
      fill: type === 'admin' ? '#ef4444' : type === 'company' ? '#3b82f6' : type === 'user' ? '#10b981' : '#8b5cf6',
    }))
  }

  const getActivityLogStatusData = () => {
    if (!activityLogStats?.logsByStatus) return []
    return Object.entries(activityLogStats.logsByStatus).map(([status, count]) => ({
      name: t(`statsDashboard.status.${status}`, { defaultValue: t('statsDashboard.common.other') }),
      value: count,
      fill: status === 'success' ? '#10b981' : status === 'error' ? '#ef4444' : status === 'pending' ? '#f59e0b' : status === 'completed' ? '#3b82f6' : '#8b5cf6',
    }))
  }

  const getActivityLogPaymentStatusData = () => {
    if (!activityLogStats?.logsByPaymentStatus) return []
    return Object.entries(activityLogStats.logsByPaymentStatus).map(([status, count]) => ({
      name: t(`statsDashboard.paymentStatus.${status}`, { defaultValue: t('statsDashboard.common.other') }),
      value: count,
      fill: status === 'paid' ? '#10b981' : status === 'pending' ? '#f59e0b' : status === 'failed' ? '#ef4444' : status === 'cancelled' ? '#8b5cf6' : '#6b7280',
    }))
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
        <h3 className="text-xl font-medium leading-6 text-gray-900 mb-4">{t('statsDashboard.dashboardStats')}</h3>
        <div className="mt-3 sm:mt-4">
          <div className="hidden sm:block">
            <nav className="-mb-px flex space-x-8 overflow-x-auto flex-nowrap">
              {tabs.map(tab => (
                <span
                  key={tab.name}
                  onClick={() => setCurrentTab(tab.id)}
                  className={classNames(
                    tab.id === params?.t ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                    'whitespace-nowrap pb-4 border-b-2 font-medium text-sm cursor-pointer flex-shrink-0'
                  )}
                  aria-current={params?.t === tab.id ? 'page' : undefined}
                >
                  {tab.name}
                </span>
              ))}
            </nav>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-6">
        <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 overflow-visible">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">{t('statsDashboard.sections.filterByDateRange')}</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="min-w-0">
              <label htmlFor="stats-filter-date-range" className="block text-sm text-gray-600 mb-1">
                {t('statsDashboard.filters.dateRange')}
              </label>
              <Popover
                custom
                placement="left"
                ref={globalDatePopoverRef}
                content={close => (
                  <DateRangePicker
                    weekStartsOn={0}
                    locale={datePickerLocale}
                    staticRanges={localizedStaticRanges}
                    inputRanges={localizedInputRanges}
                    startDatePlaceholder={t('reports.datePicker.early')}
                    endDatePlaceholder={t('reports.datePicker.continuous')}
                    footerContent={
                      <>
                        <button
                          onClick={close}
                          type="button"
                          className="w-[100px] mt-4 py-2 mr-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 outline-none"
                        >
                          {t('statsDashboard.buttons.close')}
                        </button>
                        <button
                          type="button"
                          disabled={!globalDateRange.startDate && !globalDateRange.endDate}
                          onClick={() => {
                            clearGlobalDateRange()
                            close()
                          }}
                          className="w-[100px] mt-4 py-2 mr-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {t('statsDashboard.buttons.clear')}
                        </button>
                      </>
                    }
                    ranges={[globalDateSelection]}
                    onChange={handleGlobalDateRangeChange}
                  />
                )}
              >
                <button
                  type="button"
                  id="stats-filter-date-range"
                  className="inline-flex w-full h-[40px] justify-start items-center border border-gray-300 shadow-sm text-sm leading-4 font-regular rounded-md text-gray-700 bg-white hover:bg-gray-50 outline-none transition-all px-3"
                >
                  <IoCalendar className="h-4 w-4 mr-3 text-gray-500" />
                  <span className="truncate">
                    {globalDateRange.startDate && globalDateRange.endDate
                      ? `${globalDateRange.startDate.format('MMM Do, YYYY')} - ${globalDateRange.endDate.format('MMM Do, YYYY')}`
                      : t('statsDashboard.filters.selectDateRange')}
                  </span>
                </button>
              </Popover>
            </div>
          </div>
        </div>

        {params?.t === 1 && (
          <div className="space-y-6">
            {/* Summary Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title={t('statsDashboard.cards.totalUsers')}
                value={dashboardOverview?.summary?.totalUsers?.toLocaleString() || '0'}
                change={dashboardOverview?.keyMetrics?.userGrowth != null ? percentGrowthLabel(dashboardOverview.keyMetrics.userGrowth) : null}
                icon="👥"
                trend={growthTrend(dashboardOverview?.keyMetrics?.userGrowth)}
              />
              <StatCard
                title={t('statsDashboard.cards.totalBookings')}
                value={dashboardOverview?.summary?.totalBookings?.toLocaleString() || '0'}
                change={dashboardOverview?.keyMetrics?.bookingGrowth != null ? percentGrowthLabel(dashboardOverview.keyMetrics.bookingGrowth) : null}
                icon="📅"
                trend={growthTrend(dashboardOverview?.keyMetrics?.bookingGrowth)}
              />
              <StatCard title={t('statsDashboard.cards.totalCompanies')} value={dashboardOverview?.summary?.totalCompanies?.toLocaleString() || '0'} icon="🏢" trend="up" />
              <StatCard title={t('statsDashboard.cards.totalCars')} value={dashboardOverview?.summary?.totalCars?.toLocaleString() || '0'} icon="🚗" trend="up" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title={t('statsDashboard.cards.totalRevenue')}
                value={`$${dashboardOverview?.summary?.totalRevenue?.toLocaleString() || '0'}`}
                change={dashboardOverview?.keyMetrics?.revenueGrowth != null ? percentGrowthLabel(dashboardOverview.keyMetrics.revenueGrowth) : null}
                icon="💰"
                trend={growthTrend(dashboardOverview?.keyMetrics?.revenueGrowth)}
              />
              <StatCard title={t('statsDashboard.cards.activeSessions')} value={dashboardOverview?.summary?.activeSessions?.toLocaleString() || '0'} icon="🟢" trend="up" />
              <StatCard title={t('statsDashboard.cards.systemUptime')} value={`${dashboardOverview?.keyMetrics?.systemUptime || '0'}%`} icon="⚡" trend="up" />
              <StatCard title={t('statsDashboard.cards.newUsersToday')} value={dashboardOverview?.quickStats?.newUsersToday?.toLocaleString() || '0'} icon="✨" trend="up" />
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {' '}
              <StatCard title={t('statsDashboard.cards.newBookingsToday')} value={dashboardOverview?.quickStats?.newBookingsToday?.toLocaleString() || '0'} icon="📅" trend="up" />
              <StatCard title={t('statsDashboard.cards.revenueToday')} value={`$${dashboardOverview?.quickStats?.revenueToday?.toLocaleString() || '0'}`} icon="💵" trend="up" />
            </div>

            {/* Top Performers */}
            <div className="grid grid-cols-1  gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.topRevenueCompanies')}</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={dashboardOverview?.topPerformers?.companies || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="companyName" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={3} dot={{ fill: '#10b981', strokeWidth: 2, r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.topRevenueCar')}</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={dashboardOverview?.topPerformers?.cars || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="model" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={3} dot={{ fill: '#3b82f6', strokeWidth: 2, r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Alerts Section */}
            {dashboardOverview?.alerts && dashboardOverview.alerts.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.systemAlerts')}</h3>
                <div className="space-y-4">
                  {dashboardOverview.alerts.map((alert, index) => (
                    <div
                      key={index}
                      className={`p-4 rounded-lg border-l-4 ${
                        alert.type === 'warning'
                          ? 'bg-yellow-50 border-yellow-400 text-yellow-800'
                          : alert.type === 'error'
                          ? 'bg-red-50 border-red-400 text-red-800'
                          : 'bg-blue-50 border-blue-400 text-blue-800'
                      }`}
                    >
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          {alert.type === 'warning' ? (
                            <span className="text-yellow-400">⚠️</span>
                          ) : alert.type === 'error' ? (
                            <span className="text-red-400">🚨</span>
                          ) : (
                            <span className="text-blue-400">ℹ️</span>
                          )}
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium">{alert.message}</p>
                          <p className="text-xs mt-1">
                            {t('statsDashboard.common.severity')} {t(`statsDashboard.severity.${alert.severity}`, { defaultValue: alert.severity })}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Performance Metrics */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.growthMetrics')}</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{t('statsDashboard.labels.userGrowth')}</span>
                    <span className={`font-medium ${dashboardOverview?.keyMetrics?.userGrowth > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {dashboardOverview?.keyMetrics?.userGrowth > 0 ? '+' : ''}
                      {dashboardOverview?.keyMetrics?.userGrowth || '0'}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{t('statsDashboard.labels.bookingGrowth')}</span>
                    <span className={`font-medium ${dashboardOverview?.keyMetrics?.bookingGrowth > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {dashboardOverview?.keyMetrics?.bookingGrowth > 0 ? '+' : ''}
                      {dashboardOverview?.keyMetrics?.bookingGrowth || '0'}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{t('statsDashboard.labels.revenueGrowth')}</span>
                    <span className={`font-medium ${dashboardOverview?.keyMetrics?.revenueGrowth > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {dashboardOverview?.keyMetrics?.revenueGrowth > 0 ? '+' : ''}
                      {dashboardOverview?.keyMetrics?.revenueGrowth || '0'}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{t('statsDashboard.cards.systemUptime')}</span>
                    <span className="font-medium text-green-600">{dashboardOverview?.keyMetrics?.systemUptime || '0'}%</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.todaysSummary')}</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{t('statsDashboard.labels.newUsers')}</span>
                    <span className="font-medium text-blue-600">{dashboardOverview?.quickStats?.newUsersToday || '0'}</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{t('statsDashboard.labels.newBookings')}</span>
                    <span className="font-medium text-green-600">{dashboardOverview?.quickStats?.newBookingsToday || '0'}</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{t('statsDashboard.labels.revenue')}</span>
                    <span className="font-medium text-green-600">${dashboardOverview?.quickStats?.revenueToday?.toLocaleString() || '0'}</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{t('statsDashboard.cards.activeSessions')}</span>
                    <span className="font-medium text-purple-600">{dashboardOverview?.quickStats?.activeSessions || '0'}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('statsDashboard.sections.platformAndBookingSourceAnalytics')}</h3>
              <p className="text-sm text-gray-500 mb-6">{t('statsDashboard.sections.platformAndBookingSourceAnalyticsHint')}</p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                <StatCard title={t('statsDashboard.sources.website')} value={bookingStats?.bookingsBySource?.website?.toLocaleString() || '0'} icon="🌐" trend="up" />
                <StatCard title={t('statsDashboard.sources.mobileApp')} value={bookingStats?.bookingsBySource?.mobile_app?.toLocaleString() || '0'} icon="📱" trend="up" />
                <StatCard title={t('statsDashboard.sources.adminPanel')} value={bookingStats?.bookingsBySource?.admin_panel?.toLocaleString() || '0'} icon="🛠️" trend="neutral" />
                <StatCard title={t('statsDashboard.platforms.browser')} value={bookingStats?.bookingsByPlatform?.web?.toLocaleString() || '0'} icon="💻" trend="up" />
                <StatCard title={t('statsDashboard.platforms.ios')} value={bookingStats?.bookingsByPlatform?.ios?.toLocaleString() || '0'} icon="🍎" trend="up" />
                <StatCard title={t('statsDashboard.platforms.android')} value={bookingStats?.bookingsByPlatform?.android?.toLocaleString() || '0'} icon="🤖" trend="up" />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-base font-medium text-gray-800 mb-4">{t('statsDashboard.sections.bookingSourceDistribution')}</h4>
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={getBookingSourceData()} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={5} dataKey="value">
                        {getBookingSourceData().map((entry, index) => (
                          <Cell key={`overview-source-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <h4 className="text-base font-medium text-gray-800 mb-4">{t('statsDashboard.sections.bookingPlatformDistribution')}</h4>
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={getBookingPlatformData()} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={5} dataKey="value">
                        {getBookingPlatformData().map((entry, index) => (
                          <Cell key={`overview-platform-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Top Companies Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.topPerformingCompanies')}</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.userTypes.company')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.labels.revenue')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.bookings')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {dashboardOverview?.topPerformers?.companies?.map((company, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{company.companyName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${company.revenue?.toLocaleString() || '0'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{company.bookings || '0'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Top Cars Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.topPerformingCars')}</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.car')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.labels.revenue')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {dashboardOverview?.topPerformers?.cars?.map((car, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {car.make} {car.model}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${car.revenue?.toLocaleString() || '0'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
        {params?.t === 2 && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">{t('statsDashboard.sections.userStatisticsFilters')}</h3>
                <button onClick={clearBranchStatsFilters} type="button" className="px-3 py-1 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
                  {t('statsDashboard.buttons.clearFilters')}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="user-filter-company" className="block text-sm text-gray-600 mb-1">
                    {t('statsDashboard.tables.company')}
                  </label>
                  <Selection
                    value={branchStatsFilters.companyId}
                    onChange={value => handleBranchStatsFilterChange('companyId', value?.id || '')}
                    list={carCompanyOptions}
                    className="w-full"
                    title={t('statsDashboard.filters.selectCompany')}
                  />
                </div>
                <div>
                  <label htmlFor="user-filter-language" className="block text-sm text-gray-600 mb-1">
                    {t('statsDashboard.filters.language')}
                  </label>
                  <Selection
                    value={branchStatsFilters.lang}
                    onChange={value => handleBranchStatsFilterChange('lang', value?.id || '')}
                    list={languageFilterOptions}
                    className="w-full"
                    title={t('statsDashboard.filters.language')}
                  />
                </div>
                <div>
                  <label htmlFor="user-filter-type" className="block text-sm text-gray-600 mb-1">
                    {t('statsDashboard.filters.userType')}
                  </label>
                  <Selection
                    value={branchStatsFilters.type}
                    onChange={value => handleBranchStatsFilterChange('type', value?.id || '')}
                    list={userTypeFilterOptions}
                    className="w-full"
                    title={t('statsDashboard.filters.userType')}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title={t('statsDashboard.cards.totalUsers')}
                value={stats?.totalUsers?.toLocaleString() || '0'}
                change={stats?.usersGrowth != null ? percentGrowthLabel(stats.usersGrowth) : null}
                icon="👥"
                trend={growthTrend(stats?.usersGrowth)}
              />
              <StatCard
                title={t('statsDashboard.cards.activeUsers')}
                value={stats?.activeUsers?.toLocaleString() || '0'}
                change={pctOfTotalFrom(stats?.activeUsers, stats?.totalUsers)}
                icon="🟢"
                trend="up"
              />
              <StatCard
                title={t('statsDashboard.cards.newUsersToday')}
                value={stats?.newUsersToday || '0'}
                change={stats?.usersGrowth != null ? percentGrowthLabel(stats.usersGrowth) : null}
                icon="✨"
                trend={growthTrend(stats?.usersGrowth)}
              />
              <StatCard
                title={t('statsDashboard.cards.verifiedUsers')}
                value={stats?.verifiedUsers?.toLocaleString() || '0'}
                change={pctOfTotalFrom(stats?.verifiedUsers, stats?.totalUsers)}
                icon="✅"
                trend="up"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title={t('statsDashboard.cards.inactiveUsers')}
                value={stats?.inactiveUsers?.toLocaleString() || '0'}
                change={pctOfTotalFrom(stats?.inactiveUsers, stats?.totalUsers)}
                icon="🔴"
                trend="down"
              />
              <StatCard
                title={t('statsDashboard.cards.unverifiedUsers')}
                value={stats?.unverifiedUsers?.toLocaleString() || '0'}
                change={pctOfTotalFrom(stats?.unverifiedUsers, stats?.totalUsers)}
                icon="❌"
                trend="down"
              />
              <StatCard
                title={t('statsDashboard.cards.temporaryUsers')}
                value={stats?.temporaryUsers?.toLocaleString() || '0'}
                change={pctOfTotalFrom(stats?.temporaryUsers, stats?.totalUsers)}
                icon="⏰"
                trend="neutral"
              />
              <StatCard title={t('statsDashboard.cards.deletedUsers')} value={stats?.deletedUsers?.toLocaleString() || '0'} icon="🗑️" trend="down" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title={t('statsDashboard.cards.phoneVerified')}
                value={stats?.phoneVerifiedUsers?.toLocaleString() || '0'}
                change={pctOfTotalFrom(stats?.phoneVerifiedUsers, stats?.totalUsers)}
                icon="📱"
                trend="up"
              />
              <StatCard
                title={t('statsDashboard.cards.emailVerified')}
                value={stats?.emailVerifiedUsers?.toLocaleString() || '0'}
                change={pctOfTotalFrom(stats?.emailVerifiedUsers, stats?.totalUsers)}
                icon="📧"
                trend="up"
              />
              <StatCard
                title={t('statsDashboard.cards.drivingLicenseVerified')}
                value={stats?.drivingLicenseVerifiedUsers?.toLocaleString() || '0'}
                change={pctOfTotalFrom(stats?.drivingLicenseVerifiedUsers, stats?.totalUsers)}
                icon="🪪"
                trend="up"
              />
              <StatCard
                title={t('statsDashboard.cards.usersWithWallet')}
                value={stats?.usersWithWallet?.toLocaleString() || '0'}
                change={pctOfTotalFrom(stats?.usersWithWallet, stats?.totalUsers)}
                icon="💰"
                trend="up"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title={t('statsDashboard.cards.blacklistedUsers')}
                value={stats?.blacklistedUsers?.toLocaleString() || '0'}
                change={pctOfTotalFrom(stats?.blacklistedUsers, stats?.totalUsers)}
                icon="🚫"
                trend="down"
              />
              <StatCard title={t('statsDashboard.cards.fraudUsers')} value={stats?.fraudUsers?.toLocaleString() || '0'} icon="⚠️" trend="down" />
              <StatCard
                title={t('statsDashboard.cards.activeUsersToday')}
                value={activityData?.activeUsersToday?.toLocaleString() || '0'}
                change={activityData?.activeUsersThisWeek != null ? changeThisWeek(activityData.activeUsersThisWeek) : null}
                icon="📅"
                trend="up"
              />
              <StatCard
                title={t('statsDashboard.cards.avgSessionTime')}
                value={activityData?.averageSessionDuration || '0'}
                change={t('statsDashboard.units.minutes')}
                icon="⏱️"
                trend="neutral"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              <StatCard title={t('statsDashboard.cards.activeToday')} value={activityData?.usersByLastLogin?.today || '0'} icon="📅" trend="up" />
              <StatCard title={t('statsDashboard.cards.activeThisWeek')} value={activityData?.usersByLastLogin?.thisWeek || '0'} icon="📆" trend="up" />
              <StatCard title={t('statsDashboard.cards.activeThisMonth')} value={activityData?.usersByLastLogin?.thisMonth || '0'} icon="📊" trend="up" />
              <StatCard title={t('statsDashboard.cards.last3Months')} value={activityData?.usersByLastLogin?.last3Months || '0'} icon="📈" trend="up" />
              <StatCard title={t('statsDashboard.cards.inactiveUsers')} value={activityData?.usersByLastLogin?.inactive || '0'} icon="😴" trend="down" />
            </div>

            {activeSessionStats && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard title={t('statsDashboard.cards.currentActiveSessions')} value={activeSessionStats.currentActiveSessions?.toLocaleString() || '0'} icon="🟢" trend="up" />
                <StatCard title={t('statsDashboard.cards.peakConcurrentUsers')} value={activeSessionStats.concurrentUsers?.peak?.toLocaleString() || '0'} icon="📈" trend="up" />
                <StatCard
                  title={t('statsDashboard.cards.averageConcurrentUsers')}
                  value={activeSessionStats.concurrentUsers?.average?.toLocaleString() || '0'}
                  icon="📊"
                  trend="neutral"
                />
                <StatCard title={t('statsDashboard.cards.lowConcurrentUsers')} value={activeSessionStats.concurrentUsers?.low?.toLocaleString() || '0'} icon="📉" trend="down" />
              </div>
            )}

            {devicePlatformStats && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard title={t('statsDashboard.cards.totalDevices')} value={devicePlatformStats.deviceStatistics?.totalDevices?.toLocaleString() || '0'} icon="📱" trend="up" />
                <StatCard
                  title={t('statsDashboard.cards.androidDevices')}
                  value={devicePlatformStats.deviceStatistics?.androidDevices?.toLocaleString() || '0'}
                  icon="🤖"
                  trend="up"
                />
                <StatCard title={t('statsDashboard.cards.iosDevices')} value={devicePlatformStats.deviceStatistics?.iosDevices?.toLocaleString() || '0'} icon="🍎" trend="up" />
                <StatCard title={t('statsDashboard.cards.webUsers')} value={devicePlatformStats.deviceStatistics?.webUsers?.toLocaleString() || '0'} icon="🌐" trend="up" />
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">{t('statsDashboard.sections.userGrowthTrends')}</h3>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={growthData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="period" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="newUsers" stackId="1" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
                    <Area type="monotone" dataKey="activeUsers" stackId="1" stroke="#82ca9d" fill="#82ca9d" fillOpacity={0.6} />
                    <Area type="monotone" dataKey="verifiedUsers" stackId="1" stroke="#ffc658" fill="#ffc658" fillOpacity={0.6} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.deviceDistribution')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={getDeviceData()} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                      {getDeviceData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.userTypesDistribution')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={getUserTypesData()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="type" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" fill="#8884d8" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.recentActivity')}</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{t('statsDashboard.labels.newUsersTodayLower')}</span>
                    <span className="font-medium text-gray-900">{stats?.newUsersToday || 0}</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{t('statsDashboard.labels.newUsersThisWeek')}</span>
                    <span className="font-medium text-gray-900">{stats?.newUsersThisWeek || 0}</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{t('statsDashboard.labels.newUsersThisMonth')}</span>
                    <span className="font-medium text-gray-900">{stats?.newUsersThisMonth || 0}</span>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.labels.phoneVerifiedLower')}</span>
                      <span className="font-medium text-green-600">{stats?.totalUsers ? ((stats.phoneVerifiedUsers / stats.totalUsers) * 100).toFixed(1) : 0}%</span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.labels.emailVerifiedLower')}</span>
                      <span className="font-medium text-green-600">{stats?.totalUsers ? ((stats.emailVerifiedUsers / stats.totalUsers) * 100).toFixed(1) : 0}%</span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.labels.activeTodayLower')}</span>
                      <span className="font-medium text-blue-600">{activityData?.activeUsersToday || 0}</span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.labels.avgSessionTimeLower')}</span>
                      <span className="font-medium text-purple-600">{t('statsDashboard.labels.sessionDurationValue', { minutes: activityData?.averageSessionDuration || 0 })}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.topCompaniesByUsers')}</h3>
                <ResponsiveContainer width="100%" height={450}>
                  <LineChart data={getCompanyData()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="company" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="users" stroke="#8884d8" strokeWidth={3} dot={{ fill: '#8884d8', strokeWidth: 2, r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.topBranchesByUsers')}</h3>
                <ResponsiveContainer width="100%" height={450}>
                  <LineChart data={getBranchData()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="branch" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="users" stroke="#82ca9d" strokeWidth={3} dot={{ fill: '#82ca9d', strokeWidth: 2, r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.popularNationalities')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={getNationalityData()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="category" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="count" stroke="#8884d8" strokeWidth={3} dot={{ fill: '#8884d8', strokeWidth: 2, r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.ageGroupDistribution')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={getAgeGroupData()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="ageGroup" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" fill="#ffc658" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.languageDistribution')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={getLanguageData()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="language" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" fill="#ff6b6b" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.residencyStatus')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={getResidencyData()} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                      {getResidencyData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.topActiveUsers')}</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.userTypes.user')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.loginCount')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.lastLogin')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {activityData?.topActiveUsers?.map((user, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600 hover:underline">
                          <Link to={`/users/edit/${user._id}`}>{user.fullName || user.email || user.phone || t('statsDashboard.userTypes.user')}</Link>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.loginCount}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(user.lastLogin).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {activeSessionStats && (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.sessionsByPlatform')}</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: t('statsDashboard.platforms.browser'), value: activeSessionStats.sessionsByPlatform?.web || 0, fill: '#3b82f6' },
                            { name: t('statsDashboard.platforms.android'), value: activeSessionStats.sessionsByPlatform?.android || 0, fill: '#10b981' },
                            { name: t('statsDashboard.platforms.ios'), value: activeSessionStats.sessionsByPlatform?.ios || 0, fill: '#8b5cf6' },
                          ]}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          <Cell fill="#3b82f6" />
                          <Cell fill="#10b981" />
                          <Cell fill="#8b5cf6" />
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.sessionsByUserType')}</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart
                        data={[
                          { type: t('statsDashboard.userTypes.admin'), sessions: activeSessionStats.sessionsByUserType?.admin || 0 },
                          { type: t('statsDashboard.userTypes.company'), sessions: activeSessionStats.sessionsByUserType?.company || 0 },
                          { type: t('statsDashboard.userTypes.user'), sessions: activeSessionStats.sessionsByUserType?.user || 0 },
                        ]}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="type" stroke="#666" />
                        <YAxis stroke="#666" />
                        <Tooltip content={<CustomTooltip />} />
                        <Line type="monotone" dataKey="sessions" stroke="#8884d8" strokeWidth={3} dot={{ fill: '#8884d8', strokeWidth: 2, r: 6 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.topCompaniesByActiveSessions')}</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={activeSessionStats.sessionsByCompany?.slice(0, 10) || []} layout="horizontal">
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis type="number" stroke="#666" />
                        <YAxis dataKey="companyName" type="category" stroke="#666" width={120} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="activeSessions" fill="#10b981" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.sessionsByLocation')}</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={activeSessionStats.sessionsByLocation?.slice(0, 10) || []} layout="horizontal">
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis type="number" stroke="#666" />
                        <YAxis dataKey="location" type="category" stroke="#666" width={80} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="sessionCount" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.sessionDuration')}</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between py-2">
                        <span className="text-sm text-gray-600">{t('statsDashboard.labels.average')}</span>
                        <span className="font-medium text-blue-600">{activeSessionStats.sessionDuration?.average || 0} min</span>
                      </div>
                      <div className="flex items-center justify-between py-2">
                        <span className="text-sm text-gray-600">{t('statsDashboard.labels.median')}</span>
                        <span className="font-medium text-green-600">{activeSessionStats.sessionDuration?.median || 0} min</span>
                      </div>
                      <div className="flex items-center justify-between py-2">
                        <span className="text-sm text-gray-600">{t('statsDashboard.labels.longest')}</span>
                        <span className="font-medium text-purple-600">{activeSessionStats.sessionDuration?.longest || 0} min</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.hourlySessionTrends')}</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={activeSessionStats.sessionTrends?.hourly || []}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="hour" stroke="#666" />
                        <YAxis stroke="#666" />
                        <Tooltip content={<CustomTooltip />} />
                        <Line type="monotone" dataKey="sessionCount" stroke="#3b82f6" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.dailySessionTrends')}</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={activeSessionStats.sessionTrends?.daily || []}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="day" stroke="#666" />
                        <YAxis stroke="#666" />
                        <Tooltip content={<CustomTooltip />} />
                        <Line type="monotone" dataKey="sessionCount" stroke="#10b981" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </>
            )}

            {devicePlatformStats && (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.platformUsageStatistics')}</h3>
                    <div className="space-y-4">
                      {Object.entries(devicePlatformStats.platformUsage || {}).map(([platform, data]) => (
                        <div key={platform} className="border-b pb-3 last:border-b-0">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700 capitalize">{platform}</span>
                            <span className="text-sm text-gray-500">{data.usagePercentage}%</span>
                          </div>
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <span>
                              {t('statsDashboard.common.total')}: {data.totalUsers}
                            </span>
                            <span>
                              {t('statsDashboard.common.active')}: {data.activeUsers}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                            <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${data.usagePercentage}%` }}></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.devicesByUserType')}</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart
                        data={[
                          {
                            type: t('statsDashboard.userTypes.admin'),
                            total: devicePlatformStats.devicesByUserType?.admin?.total || 0,
                            web: devicePlatformStats.devicesByUserType?.admin?.web || 0,
                            mobile: devicePlatformStats.devicesByUserType?.admin?.mobile || 0,
                          },
                          {
                            type: t('statsDashboard.userTypes.company'),
                            total: devicePlatformStats.devicesByUserType?.company?.total || 0,
                            web: devicePlatformStats.devicesByUserType?.company?.web || 0,
                            mobile: devicePlatformStats.devicesByUserType?.company?.mobile || 0,
                          },
                          {
                            type: t('statsDashboard.userTypes.user'),
                            total: devicePlatformStats.devicesByUserType?.user?.total || 0,
                            web: devicePlatformStats.devicesByUserType?.user?.web || 0,
                            android: devicePlatformStats.devicesByUserType?.user?.android || 0,
                            ios: devicePlatformStats.devicesByUserType?.user?.ios || 0,
                          },
                        ]}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="type" stroke="#666" />
                        <YAxis stroke="#666" />
                        <Tooltip content={<CustomTooltip />} />
                        <Line type="monotone" dataKey="total" stroke="#8884d8" strokeWidth={3} dot={{ fill: '#8884d8', strokeWidth: 2, r: 6 }} />
                        <Line type="monotone" dataKey="web" stroke="#82ca9d" strokeWidth={3} dot={{ fill: '#82ca9d', strokeWidth: 2, r: 6 }} />
                        <Line type="monotone" dataKey="mobile" stroke="#ffc658" strokeWidth={3} dot={{ fill: '#ffc658', strokeWidth: 2, r: 6 }} />
                        <Line type="monotone" dataKey="android" stroke="#10b981" strokeWidth={3} dot={{ fill: '#10b981', strokeWidth: 2, r: 6 }} />
                        <Line type="monotone" dataKey="ios" stroke="#8b5cf6" strokeWidth={3} dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 6 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.topCompaniesByDevices')}</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={devicePlatformStats.devicesByCompany?.slice(0, 10) || []} layout="horizontal">
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis type="number" stroke="#666" />
                        <YAxis dataKey="companyName" type="category" stroke="#666" width={120} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="totalDevices" fill="#8884d8" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.popularDeviceModels')}</h3>
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">{t('statsDashboard.subsections.androidModels')}</h4>
                        <div className="space-y-2">
                          {devicePlatformStats.deviceModels?.android?.slice(0, 5).map((model, index) => (
                            <div key={index} className="flex items-center justify-between text-xs">
                              <span className="text-gray-600">{model.model}</span>
                              <span className="text-gray-500">
                                {model.count} ({model.percentage}%)
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="border-t pt-3">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">{t('statsDashboard.subsections.iosModels')}</h4>
                        <div className="space-y-2">
                          {devicePlatformStats.deviceModels?.ios?.slice(0, 5).map((model, index) => (
                            <div key={index} className="flex items-center justify-between text-xs">
                              <span className="text-gray-600">{model.model}</span>
                              <span className="text-gray-500">
                                {model.count} ({model.percentage}%)
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.osVersionDistribution')}</h3>
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">{t('statsDashboard.subsections.androidVersions')}</h4>
                        <div className="space-y-2">
                          {devicePlatformStats.osVersions?.android?.slice(0, 5).map((version, index) => (
                            <div key={index} className="flex items-center justify-between text-xs">
                              <span className="text-gray-600">{version.version}</span>
                              <span className="text-gray-500">
                                {version.count} ({version.percentage}%)
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="border-t pt-3">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">{t('statsDashboard.subsections.iosVersions')}</h4>
                        <div className="space-y-2">
                          {devicePlatformStats.osVersions?.ios?.slice(0, 5).map((version, index) => (
                            <div key={index} className="flex items-center justify-between text-xs">
                              <span className="text-gray-600">{version.version}</span>
                              <span className="text-gray-500">
                                {version.count} ({version.percentage}%)
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.deviceStatisticsSummary')}</h3>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="text-center p-3 bg-blue-50 rounded-lg">
                          <div className="text-2xl font-bold text-blue-600">{devicePlatformStats.deviceStatistics?.androidDevices || 0}</div>
                          <div className="text-gray-600">{t('statsDashboard.platforms.android')}</div>
                        </div>
                        <div className="text-center p-3 bg-green-50 rounded-lg">
                          <div className="text-2xl font-bold text-green-600">{devicePlatformStats.deviceStatistics?.iosDevices || 0}</div>
                          <div className="text-gray-600">{t('statsDashboard.platforms.ios')}</div>
                        </div>
                        <div className="text-center p-3 bg-purple-50 rounded-lg">
                          <div className="text-2xl font-bold text-purple-600">{devicePlatformStats.deviceStatistics?.webUsers || 0}</div>
                          <div className="text-gray-600">{t('statsDashboard.platforms.browser')}</div>
                        </div>
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <div className="text-2xl font-bold text-gray-600">{devicePlatformStats.deviceStatistics?.totalDevices || 0}</div>
                          <div className="text-gray-600">{t('statsDashboard.common.total')}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {params?.t === 3 && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">{t('statsDashboard.sections.companyFilters')}</h3>
                <button onClick={clearCompanyFilters} type="button" className="px-3 py-1 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
                  {t('statsDashboard.buttons.clearFilters')}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label htmlFor="company-filter-company" className="block text-sm text-gray-600 mb-1">
                    {t('statsDashboard.userTypes.company')}
                  </label>
                  <Selection
                    value={companyFilters.companyId}
                    onChange={value => handleCompanyFilterChange('companyId', value?.id || '')}
                    list={companySingleFilterOptions}
                    className="w-full"
                    title={t('statsDashboard.filters.selectCompany')}
                  />
                </div>
                <div>
                  <label htmlFor="company-filter-governorate" className="block text-sm text-gray-600 mb-1">
                    {t('statsDashboard.filters.governorate')}
                  </label>
                  <Selection
                    value={companyFilters.governorate}
                    onChange={value => handleCompanyFilterChange('governorate', value?.id || '')}
                    list={governorateSingleFilterOptions}
                    className="w-full"
                    title={t('statsDashboard.filters.selectGovernorate')}
                  />
                </div>
                <div />
                <div />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title={t('statsDashboard.cards.totalCompanies')}
                value={companyStats?.totalCompanies?.toLocaleString() || '0'}
                change={companyStats?.companiesGrowth != null ? percentGrowthLabel(companyStats.companiesGrowth) : null}
                icon="🏢"
                trend={growthTrend(companyStats?.companiesGrowth)}
              />
              <StatCard title={t('statsDashboard.cards.newCompaniesThisMonth')} value={companyStats?.newCompaniesThisMonth || '0'} icon="✨" trend="up" />
              <StatCard
                title={t('statsDashboard.cards.activeCompanies')}
                value={companyStats?.companiesByPerformance?.filter(c => c.totalCars > 0).length || '0'}
                icon="🟢"
                trend="up"
              />
              <StatCard
                title={t('statsDashboard.cards.companiesWithCars')}
                value={companyStats?.companiesByCarCount?.filter(c => c.totalCars > 0).length || '0'}
                icon="🚗"
                trend="up"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard title={t('statsDashboard.cards.avgAcceptanceRate')} value={getAverageAcceptanceRate()} icon="✅" trend="up" />
              <StatCard title={t('statsDashboard.cards.avgUtilizationRate')} value={getAverageUtilizationRate()} icon="📊" trend="up" />
              <StatCard title={t('statsDashboard.cards.avgResponseTime')} value="2.5" change={t('statsDashboard.units.hours')} icon="⏱️" trend="neutral" />
              <StatCard title={t('statsDashboard.cards.avgCustomerRating')} value="4.5" icon="⭐" trend="up" />
            </div>

            <div className="grid grid-cols-1 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.topCompaniesByRevenue')}</h3>
                <ResponsiveContainer width="100%" height={450}>
                  <LineChart data={companyStats?.topCompaniesByRevenue || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="companyName" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={3} dot={{ fill: '#10b981', strokeWidth: 2, r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.topCompaniesByBookings')}</h3>
                <ResponsiveContainer width="100%" height={450}>
                  <LineChart data={companyStats?.topCompaniesByBookings || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="companyName" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="bookingCount" stroke="#3b82f6" strokeWidth={3} dot={{ fill: '#3b82f6', strokeWidth: 2, r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.companiesByCarCount')}</h3>
                <ResponsiveContainer width="100%" height={450}>
                  <LineChart data={companyStats?.companiesByCarCount || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="companyName" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="totalCars" stroke="#f59e0b" strokeWidth={3} dot={{ fill: '#f59e0b', strokeWidth: 2, r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.companiesByRating')}</h3>
                <ResponsiveContainer width="100%" height={450}>
                  <LineChart data={companyStats?.companiesByRating || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="companyName" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="averageRating" stroke="#8b5cf6" strokeWidth={3} dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.companyPerformanceOverview')}</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.userTypes.company')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.totalCars')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.activeCars')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.totalBookings')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.labels.revenue')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.acceptancePercent')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.rating')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {companyStats?.companiesByPerformance?.map((company, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{company.companyName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{company.totalCars}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{company.activeCars}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{company.totalBookings}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${company.totalRevenue?.toLocaleString() || '0'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{company.acceptanceRatio}%</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{company.averageRating}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.companiesByGovernorate')}</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={getCompanyGovernorateData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="governorate" stroke="#666" />
                  <YAxis stroke="#666" />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {params?.t === 4 && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">{t('statsDashboard.sections.carStatisticsFilters')}</h3>
                <button onClick={clearCarFilters} type="button" className="px-3 py-1 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
                  {t('statsDashboard.buttons.clearFilters')}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label htmlFor="car-filter-company" className="block text-sm text-gray-600 mb-1">
                    {t('statsDashboard.userTypes.company')}
                  </label>
                  <Selection
                    value={carFilters.companyId}
                    onChange={value => handleCarFilterChange('companyId', value?.id || '')}
                    list={carCompanyOptions}
                    className="w-full"
                    title={t('statsDashboard.filters.selectCompany')}
                  />
                </div>
                <div>
                  <label htmlFor="car-filter-branch" className="block text-sm text-gray-600 mb-1">
                    {t('statsDashboard.filters.branch')}
                  </label>
                  <Selection
                    value={carFilters.branchId}
                    onChange={value => handleCarFilterChange('branchId', value?.id || '')}
                    list={carBranchOptions}
                    className="w-full"
                    title={t('statsDashboard.filters.selectBranch')}
                    disabled={!carFilters.companyId}
                  />
                </div>
                <div>
                  <label htmlFor="car-filter-segment" className="block text-sm text-gray-600 mb-1">
                    {t('statsDashboard.filters.marketSegment')}
                  </label>
                  <Selection
                    value={carFilters.marketSegment}
                    onChange={value => handleCarFilterChange('marketSegment', value?.id || '')}
                    list={marketSegmentFilterOptions}
                    className="w-full"
                    title={t('statsDashboard.filters.selectMarketSegment')}
                  />
                </div>
                <div>
                  <label htmlFor="car-filter-status" className="block text-sm text-gray-600 mb-1">
                    {t('statsDashboard.filters.status')}
                  </label>
                  <Selection
                    value={carFilters.status}
                    onChange={value => handleCarFilterChange('status', value?.id || '')}
                    list={carStatusFilterOptions}
                    className="w-full"
                    title={t('statsDashboard.filters.selectStatus')}
                  />
                </div>
                <div>
                  <label htmlFor="car-filter-insurance" className="block text-sm text-gray-600 mb-1">
                    {t('statsDashboard.filters.insurance')}
                  </label>
                  <Selection
                    value={carFilters.insurance}
                    onChange={value => handleCarFilterChange('insurance', value?.id || '')}
                    list={insuranceFilterOptions}
                    className="w-full"
                    title={t('statsDashboard.filters.selectInsurance')}
                  />
                </div>
                <div>
                  <label htmlFor="car-filter-fuel-policy" className="block text-sm text-gray-600 mb-1">
                    {t('statsDashboard.filters.fuelPolicy')}
                  </label>
                  <Selection
                    value={carFilters.fuelPolicy}
                    onChange={value => handleCarFilterChange('fuelPolicy', value?.id || '')}
                    list={fuelPolicyFilterOptions}
                    className="w-full"
                    title={t('statsDashboard.filters.selectFuelPolicy')}
                  />
                </div>
                <div>
                  <label htmlFor="car-filter-date-range" className="block text-sm text-gray-600 mb-1">
                    {t('statsDashboard.filters.dateRange')}
                  </label>
                  <Popover
                    custom
                    placement="left"
                    ref={carDatePopoverRef}
                    content={close => (
                      <div className="p-4">
                        <DateRangePicker
                          ranges={[
                            {
                              startDate: carDateRange.startDate ? carDateRange.startDate.toDate() : new Date(),
                              endDate: carDateRange.endDate ? carDateRange.endDate.toDate() : new Date(),
                              key: 'selection',
                            },
                          ]}
                          onChange={handleCarDateRangeChange}
                        />
                        <div className="flex justify-end mt-4 gap-2">
                          <button
                            onClick={close}
                            type="button"
                            className="px-4 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 outline-none"
                          >
                            {t('statsDashboard.buttons.close')}
                          </button>
                          <button
                            type="button"
                            disabled={!carDateRange.startDate && !carDateRange.endDate}
                            onClick={() => {
                              setCarDateRange({ startDate: null, endDate: null })
                              handleCarFilterChange('startDate', '')
                              handleCarFilterChange('endDate', '')
                            }}
                            className="px-4 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {t('statsDashboard.buttons.clear')}
                          </button>
                        </div>
                      </div>
                    )}
                  >
                    <button
                      type="button"
                      id="car-filter-date-range"
                      className="inline-flex w-full h-[40px] justify-start items-center border border-gray-300 shadow-sm text-sm leading-4 font-regular rounded-md text-gray-700 bg-white hover:bg-gray-50 outline-none transition-all px-3"
                    >
                      <IoCalendar className="h-4 w-4 mr-3 text-gray-500" />
                      <span className="truncate">
                        {carDateRange.startDate && carDateRange.endDate
                          ? `${carDateRange.startDate.format('MM-DD-YYYY')} to ${carDateRange.endDate.format('MM-DD-YYYY')}`
                          : t('statsDashboard.filters.selectDateRange')}
                      </span>
                    </button>
                  </Popover>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard title={t('statsDashboard.cards.totalCars')} value={carStats?.totalCars?.toLocaleString() || '0'} icon="🚗" trend="up" />
              <StatCard title={t('statsDashboard.cards.activeCars')} value={carStats?.activeCars?.toLocaleString() || '0'} icon="🟢" trend="up" />
              <StatCard title={t('statsDashboard.cards.bookedCars')} value={carStats?.bookedCars?.toLocaleString() || '0'} icon="📅" trend="up" />
              <StatCard title={t('statsDashboard.cards.outOfService')} value={carStats?.outOfServiceCars?.toLocaleString() || '0'} icon="🔧" trend="neutral" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title={t('statsDashboard.cards.avgDailyRate')}
                value={carStats?.averageDailyRate?.toLocaleString() || '0'}
                change={t('statsDashboard.units.omr')}
                icon="💰"
                trend="up"
              />
              <StatCard
                title={t('statsDashboard.cards.avgWeeklyRate')}
                value={carStats?.averageWeeklyRate?.toLocaleString() || '0'}
                change={t('statsDashboard.units.omr')}
                icon="📊"
                trend="up"
              />
              <StatCard
                title={t('statsDashboard.cards.avgMonthlyRate')}
                value={carStats?.averageMonthlyRate?.toLocaleString() || '0'}
                change={t('statsDashboard.units.omr')}
                icon="📈"
                trend="up"
              />
              <StatCard
                title={t('statsDashboard.cards.avgYearlyRate')}
                value={carStats?.averageYearlyRate?.toLocaleString() || '0'}
                change={t('statsDashboard.units.omr')}
                icon="🎯"
                trend="up"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title={t('statsDashboard.cards.avgBookingDuration')}
                value={carPerformanceStats?.carEfficiencyMetrics?.averageBookingDuration || '0'}
                icon="📊"
                trend="neutral"
              />
              <StatCard
                title={t('statsDashboard.cards.avgRevenuePerDay')}
                value={carPerformanceStats?.carEfficiencyMetrics?.averageRevenuePerDay?.toLocaleString() || '0'}
                change={t('statsDashboard.units.omr')}
                icon="💰"
                trend="up"
              />
              <StatCard title={t('statsDashboard.cards.avgCustomerRating')} value={carPerformanceStats?.carEfficiencyMetrics?.averageCustomerRating || '0'} icon="⭐" trend="up" />
              <StatCard
                title={t('statsDashboard.cards.totalRevenue')}
                value={carPerformanceStats?.carsByRevenue?.reduce((sum, car) => sum + (car.revenue || 0), 0)?.toLocaleString() || '0'}
                change={t('statsDashboard.units.omr')}
                icon="💵"
                trend="up"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.carsByFuelType')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={getCarFuelData()} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                      {getCarFuelData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.carsByTransmission')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={getCarTransmissionData()} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                      {getCarTransmissionData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.carsByUtilizationLevel')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={getCarUtilizationData()} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                      {getCarUtilizationData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.carsByMaintenanceStatus')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={getCarMaintenanceData()} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                      {getCarMaintenanceData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.bookingStatusDistribution')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={getBookingStatusData()} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                      {getBookingStatusData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.bookingSourceDistribution')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={getBookingSourceData()} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                      {getBookingSourceData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.bookingPlatformDistribution')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={getBookingPlatformData()} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                      {getBookingPlatformData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.bookingDurationDistribution')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={getBookingDurationData()} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                      {getBookingDurationData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.customerSatisfactionRatingDistribution')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={getRatingDistributionData()} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                      {getRatingDistributionData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.carsByAgeGroup')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={getCarAgeData()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" fill="#8884d8" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.carsByCondition')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={getCarConditionData()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" fill="#ffc658" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="grid grid-cols-1  gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.topCompaniesByCarCount')}</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={carStats?.carsByCompany?.slice(0, 10) || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="companyName" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="totalCars" stroke="#10b981" strokeWidth={3} dot={{ fill: '#10b981', strokeWidth: 2, r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.topBranchesByCarCount')}</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={carStats?.carsByBranch?.slice(0, 10) || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="branchName" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="totalCars" stroke="#3b82f6" strokeWidth={3} dot={{ fill: '#3b82f6', strokeWidth: 2, r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.carsByMarketSegment')}</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={getCarMarketSegmentData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" stroke="#666" />
                  <YAxis stroke="#666" />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.topRentedCars')}</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.make')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.model')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.userTypes.company')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.bookings')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.labels.revenue')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.utilizationPercent')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.rating')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {carPerformanceStats?.topRentedCars?.map((car, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{car.make}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{car.model}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{car.companyName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{car.bookingCount}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${car.revenue?.toLocaleString()}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{car.utilizationRate}%</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{car.averageRating}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.topCarsByRevenue')}</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={carPerformanceStats?.carsByRevenue?.slice(0, 10) || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="model" stroke="#666" />
                  <YAxis stroke="#666" />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="revenue" stroke="#f59e0b" strokeWidth={3} dot={{ fill: '#f59e0b', strokeWidth: 2, r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {params?.t === 5 && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">{t('statsDashboard.sections.bookingFilters')}</h3>
                <button onClick={clearBookingFilters} type="button" className="px-3 py-1 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
                  {t('statsDashboard.buttons.clearFilters')}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label htmlFor="booking-filter-source" className="block text-sm text-gray-600 mb-1">
                    {t('statsDashboard.filters.bookingSource')}
                  </label>
                  <Selection
                    value={bookingFilters.source}
                    onChange={value => handleBookingFilterChange('source', value?.id || '')}
                    list={bookingSourceFilterOptions}
                    className="w-full"
                    title={t('statsDashboard.filters.selectBookingSource')}
                  />
                </div>

                <div>
                  <label htmlFor="booking-filter-platform" className="block text-sm text-gray-600 mb-1">
                    {t('statsDashboard.filters.platform')}
                  </label>
                  <Selection
                    value={bookingFilters.platform}
                    onChange={value => handleBookingFilterChange('platform', value?.id || '')}
                    list={bookingPlatformFilterOptions}
                    className="w-full"
                    title={t('statsDashboard.filters.selectPlatform')}
                  />
                </div>

                <div>
                  <label htmlFor="booking-filter-status" className="block text-sm text-gray-600 mb-1">
                    {t('statsDashboard.filters.bookingStatus')}
                  </label>
                  <Selection
                    value={bookingFilters.bookingStatus}
                    onChange={value => handleBookingFilterChange('bookingStatus', value?.id || '')}
                    list={bookingStatusFilterOptions}
                    className="w-full"
                    title={t('statsDashboard.filters.selectBookingStatus')}
                  />
                </div>

                <div>
                  <label htmlFor="booking-filter-payment-status" className="block text-sm text-gray-600 mb-1">
                    {t('statsDashboard.filters.paymentStatus')}
                  </label>
                  <Selection
                    value={bookingFilters.paymentStatus}
                    onChange={value => handleBookingFilterChange('paymentStatus', value?.id || '')}
                    list={bookingPaymentStatusFilterOptions}
                    className="w-full"
                    title={t('statsDashboard.filters.selectPaymentStatus')}
                  />
                </div>

                <div>
                  <label htmlFor="booking-filter-company" className="block text-sm text-gray-600 mb-1">
                    {t('statsDashboard.filters.companyName')}
                  </label>
                  <Selection
                    multiple
                    value={bookingFilters.companyIds}
                    onChange={ids => handleBookingFilterChange('companyIds', ids || [])}
                    list={bookingCompanyMultiFilterOptions}
                    className="w-full"
                    title={t('statsDashboard.filters.selectCompanies')}
                  />
                </div>

                <div>
                  <label htmlFor="booking-filter-company-location" className="block text-sm text-gray-600 mb-1">
                    {t('statsDashboard.filters.locationOfCompany')}
                  </label>
                  <Selection
                    multiple
                    value={bookingFilters.companyGovernorates}
                    onChange={ids => handleBookingFilterChange('companyGovernorates', ids || [])}
                    list={governorateMultiFilterOptions}
                    className="w-full"
                    title={t('statsDashboard.filters.selectGovernorates')}
                  />
                </div>

                <div>
                  <label htmlFor="booking-filter-company-min-age" className="block text-sm text-gray-600 mb-1">
                    {t('statsDashboard.filters.minAge')}
                  </label>
                  <input
                    id="booking-filter-company-min-age"
                    type="number"
                    min="0"
                    value={bookingFilters.companyMinAge}
                    onChange={e => handleBookingFilterChange('companyMinAge', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    placeholder={t('statsDashboard.filters.minAgePlaceholder')}
                  />
                </div>

                <div>
                  <label htmlFor="booking-filter-company-max-age" className="block text-sm text-gray-600 mb-1">
                    {t('statsDashboard.filters.maxAge')}
                  </label>
                  <input
                    id="booking-filter-company-max-age"
                    type="number"
                    min="0"
                    value={bookingFilters.companyMaxAge}
                    onChange={e => handleBookingFilterChange('companyMaxAge', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    placeholder={t('statsDashboard.filters.maxAgePlaceholder')}
                  />
                </div>

                <div>
                  <label htmlFor="booking-filter-blocked-countries" className="block text-sm text-gray-600 mb-1">
                    {t('statsDashboard.filters.blockedCountries')}
                  </label>
                  <Selection
                    multiple
                    value={bookingFilters.blockedCountries}
                    onChange={ids => handleBookingFilterChange('blockedCountries', ids || [])}
                    list={blockedCountriesFilterOptions}
                    className="w-full"
                    title={t('statsDashboard.filters.selectBlockedCountries')}
                    showSearch
                  />
                </div>

                <div>
                  <label htmlFor="booking-filter-ajarlee-percentage-range" className="block text-sm text-gray-600 mb-1">
                    {t('statsDashboard.filters.ajarleePercentageRange')}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      id="booking-filter-ajarlee-percentage-range"
                      type="number"
                      min="0"
                      value={bookingFilters.ajarleePercentageMin}
                      onChange={e => handleBookingFilterChange('ajarleePercentageMin', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      placeholder={t('statsDashboard.filters.from')}
                    />
                    <input
                      type="number"
                      min="0"
                      value={bookingFilters.ajarleePercentageMax}
                      onChange={e => handleBookingFilterChange('ajarleePercentageMax', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      placeholder={t('statsDashboard.filters.to')}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="booking-filter-company-deposit-range" className="block text-sm text-gray-600 mb-1">
                    {t('statsDashboard.filters.depositAmountRange')}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      id="booking-filter-company-deposit-range"
                      type="number"
                      min="0"
                      value={bookingFilters.companyDepositMin}
                      onChange={e => handleBookingFilterChange('companyDepositMin', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      placeholder={t('statsDashboard.filters.from')}
                    />
                    <input
                      type="number"
                      min="0"
                      value={bookingFilters.companyDepositMax}
                      onChange={e => handleBookingFilterChange('companyDepositMax', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      placeholder={t('statsDashboard.filters.to')}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="booking-filter-vat-tax" className="block text-sm text-gray-600 mb-1">
                    {t('statsDashboard.filters.vatTax')}
                  </label>
                  <Selection
                    value={bookingFilters.vatTax}
                    onChange={value => handleBookingFilterChange('vatTax', value?.id || '')}
                    list={booleanBookingFilterOptions}
                    className="w-full"
                    title={t('statsDashboard.filters.vatTax')}
                  />
                </div>

                <div>
                  <label htmlFor="booking-filter-delivery" className="block text-sm text-gray-600 mb-1">
                    {t('statsDashboard.filters.delivery')}
                  </label>
                  <Selection
                    value={bookingFilters.delivery}
                    onChange={value => handleBookingFilterChange('delivery', value?.id || '')}
                    list={booleanBookingFilterOptions}
                    className="w-full"
                    title={t('statsDashboard.filters.delivery')}
                  />
                </div>

                <div>
                  <label htmlFor="booking-filter-instant-booking" className="block text-sm text-gray-600 mb-1">
                    {t('statsDashboard.filters.instantBooking')}
                  </label>
                  <Selection
                    value={bookingFilters.instantBooking}
                    onChange={value => handleBookingFilterChange('instantBooking', value?.id || '')}
                    list={booleanBookingFilterOptions}
                    className="w-full"
                    title={t('statsDashboard.filters.instantBooking')}
                  />
                </div>

                <div>
                  <label htmlFor="booking-filter-delivery-fee-range" className="block text-sm text-gray-600 mb-1">
                    {t('statsDashboard.filters.deliveryFeesRange')}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      id="booking-filter-delivery-fee-range"
                      type="number"
                      min="0"
                      value={bookingFilters.deliveryFeeMin}
                      onChange={e => handleBookingFilterChange('deliveryFeeMin', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      placeholder={t('statsDashboard.filters.from')}
                    />
                    <input
                      type="number"
                      min="0"
                      value={bookingFilters.deliveryFeeMax}
                      onChange={e => handleBookingFilterChange('deliveryFeeMax', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      placeholder={t('statsDashboard.filters.to')}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title={t('statsDashboard.cards.totalBookings')}
                value={bookingStats?.totalBookings?.toLocaleString() || '0'}
                icon="📅"
                trend="up"
                description={t('statsDashboard.descriptions.totalBookings', { scope: bookingStatusScope })}
              />
              <StatCard
                title={t('statsDashboard.cards.activeBookings')}
                value={bookingStats?.activeBookings?.toLocaleString() || '0'}
                icon="🟢"
                trend="up"
                description={t('statsDashboard.descriptions.activeBookings')}
              />
              <StatCard
                title={t('statsDashboard.cards.pendingBookings')}
                value={bookingStats?.pendingBookings?.toLocaleString() || '0'}
                icon="⏳"
                trend="up"
                description={t('statsDashboard.descriptions.pendingBookings')}
              />
              <StatCard
                title={t('statsDashboard.cards.completedBookings')}
                value={bookingStats?.completedBookings?.toLocaleString() || '0'}
                icon="✅"
                trend="up"
                description={t('statsDashboard.descriptions.completedBookings')}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title={t('statsDashboard.cards.cancelledBookings')}
                value={bookingStats?.cancelledBookings?.toLocaleString() || '0'}
                icon="❌"
                trend="down"
                description={t('statsDashboard.descriptions.cancelledBookings')}
              />
              <StatCard
                title={t('statsDashboard.cards.totalRevenue')}
                value={bookingStats?.totalRevenue?.toLocaleString() || '0'}
                change={t('statsDashboard.units.omr')}
                icon="💰"
                trend="up"
                description={t('statsDashboard.descriptions.totalRevenue', { scope: bookingStatusScope })}
              />
              <StatCard
                title={t('statsDashboard.cards.avgBookingValue')}
                value={bookingStats?.averageBookingValue?.toLocaleString() || '0'}
                change={t('statsDashboard.units.omr')}
                icon="📊"
                trend="up"
                description={t('statsDashboard.descriptions.avgBookingValue', { scope: bookingStatusScope })}
              />
              <StatCard
                title={t('statsDashboard.cards.cancellationRate')}
                value={bookingStats?.cancellationRate || '0'}
                change={t('statsDashboard.units.percent')}
                icon="📉"
                trend="down"
                description={t('statsDashboard.descriptions.cancellationRate', { scope: bookingStatusScope })}
              />
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900">{t('statsDashboard.sections.bookingTrends')}</h3>
                <p className="mt-1 text-sm text-gray-500">{t('statsDashboard.descriptions.bookingTrends')}</p>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={bookingTrends?.trendData || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="period" stroke="#666" />
                  <YAxis stroke="#666" />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="totalBookings" stroke="#8884d8" strokeWidth={2} />
                  <Line type="monotone" dataKey="revenue" stroke="#82ca9d" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('statsDashboard.sections.platformAndBookingSourceAnalytics')}</h3>
                <p className="text-sm text-gray-500 mb-6">{t('statsDashboard.descriptions.platformAndBookingSourceAnalytics')}</p>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <StatCard
                    title={t('statsDashboard.sources.website')}
                    value={bookingStats?.bookingsBySource?.website?.toLocaleString() || '0'}
                    icon="🌐"
                    trend="up"
                    description={t('statsDashboard.descriptions.sourceWebsite')}
                  />
                  <StatCard
                    title={t('statsDashboard.sources.mobileApp')}
                    value={bookingStats?.bookingsBySource?.mobile_app?.toLocaleString() || '0'}
                    icon="📱"
                    trend="up"
                    description={t('statsDashboard.descriptions.sourceMobileApp')}
                  />
                  <StatCard
                    title={t('statsDashboard.sources.adminPanel')}
                    value={bookingStats?.bookingsBySource?.admin_panel?.toLocaleString() || '0'}
                    icon="🛠️"
                    trend="neutral"
                    description={t('statsDashboard.descriptions.sourceAdminPanel')}
                  />
                  <StatCard
                    title={t('statsDashboard.sources.companyDashboard')}
                    value={bookingStats?.bookingsBySource?.company_dashboard?.toLocaleString() || '0'}
                    icon="🏢"
                    trend="neutral"
                    description={t('statsDashboard.descriptions.sourceCompanyDashboard')}
                  />
                  <StatCard
                    title={t('statsDashboard.platforms.browser')}
                    value={bookingStats?.bookingsByPlatform?.web?.toLocaleString() || '0'}
                    icon="💻"
                    trend="up"
                    description={t('statsDashboard.descriptions.platformBrowser')}
                  />
                  <StatCard
                    title={t('statsDashboard.cards.appPlatforms')}
                    value={((bookingStats?.bookingsByPlatform?.ios || 0) + (bookingStats?.bookingsByPlatform?.android || 0)).toLocaleString()}
                    icon="📲"
                    trend="up"
                    description={t('statsDashboard.descriptions.platformApp')}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900">{t('statsDashboard.sections.bookingSourceDistribution')}</h3>
                  <p className="mt-1 mb-6 text-sm text-gray-500">{t('statsDashboard.descriptions.bookingSourceDistribution')}</p>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie data={getBookingSourceData()} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                        {getBookingSourceData().map((entry, index) => (
                          <Cell key={`booking-source-pie-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900">{t('statsDashboard.sections.bookingPlatformDistribution')}</h3>
                  <p className="mt-1 mb-6 text-sm text-gray-500">{t('statsDashboard.descriptions.bookingPlatformDistribution')}</p>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie data={getBookingPlatformData()} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                        {getBookingPlatformData().map((entry, index) => (
                          <Cell key={`booking-platform-pie-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900">{t('statsDashboard.sections.bookingsBySourceComparison')}</h3>
                  <p className="mt-1 mb-6 text-sm text-gray-500">{t('statsDashboard.descriptions.bookingsBySourceComparison')}</p>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={getBookingSourceData(true).filter(d => includes(['website', 'mobile_app', 'admin_panel', 'company_dashboard'], d.key))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" stroke="#666" />
                      <YAxis stroke="#666" allowDecimals={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {getBookingSourceData(true)
                          .filter(d => includes(['website', 'mobile_app', 'admin_panel', 'company_dashboard'], d.key))
                          .map((entry, index) => (
                            <Cell key={`source-bar-${index}`} fill={entry.fill} />
                          ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900">{t('statsDashboard.sections.bookingsByPlatformComparison')}</h3>
                  <p className="mt-1 mb-6 text-sm text-gray-500">{t('statsDashboard.descriptions.bookingsByPlatformComparison')}</p>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={getBookingPlatformData(true).filter(d => includes(['web', 'ios', 'android'], d.key))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" stroke="#666" />
                      <YAxis stroke="#666" allowDecimals={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {getBookingPlatformData(true)
                          .filter(d => includes(['web', 'ios', 'android'], d.key))
                          .map((entry, index) => (
                            <Cell key={`platform-bar-${index}`} fill={entry.fill} />
                          ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900">{t('statsDashboard.sections.bookingStatusDistribution')}</h3>
                <p className="mt-1 mb-6 text-sm text-gray-500">{t('statsDashboard.descriptions.bookingStatusDistribution')}</p>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={getBookingStatusData()} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                      {getBookingStatusData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900">{t('statsDashboard.sections.bookingDurationDistribution')}</h3>
                <p className="mt-1 mb-6 text-sm text-gray-500">{t('statsDashboard.descriptions.bookingDurationDistribution')}</p>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={getBookingDurationData()} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                      {getBookingDurationData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900">{t('statsDashboard.sections.customerSatisfactionRatingDistribution')}</h3>
              <p className="mt-1 mb-6 text-sm text-gray-500">{t('statsDashboard.descriptions.customerSatisfaction')}</p>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={getRatingDistributionData()} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                    {getRatingDistributionData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900">{t('statsDashboard.sections.topCompaniesByBookings')}</h3>
              <p className="mt-1 mb-6 text-sm text-gray-500">{t('statsDashboard.descriptions.topCompaniesByBookings', { scope: bookingStatusScope })}</p>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={bookingStats?.bookingsByCompany?.slice(0, 10) || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="companyName" stroke="#666" />
                  <YAxis stroke="#666" />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="totalBookings" stroke="#10b981" strokeWidth={3} dot={{ fill: '#10b981', strokeWidth: 2, r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900">{t('statsDashboard.sections.topCompaniesByRevenue')}</h3>
              <p className="mt-1 mb-6 text-sm text-gray-500">{t('statsDashboard.descriptions.topCompaniesByRevenue', { scope: bookingStatusScope })}</p>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={bookingStats?.topPerformingCompanies?.slice(0, 10) || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="companyName" stroke="#666" />
                  <YAxis stroke="#666" />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="revenue" stroke="#f59e0b" strokeWidth={3} dot={{ fill: '#f59e0b', strokeWidth: 2, r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-1  gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900">{t('statsDashboard.sections.acceptanceRatiosByCompany')}</h3>
                <p className="mt-1 mb-6 text-sm text-gray-500">{t('statsDashboard.descriptions.acceptanceRatiosByCompany', { scope: bookingStatusScope })}</p>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={bookingPerformance?.acceptanceRatios?.byCompany?.slice(0, 10) || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="companyName" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="acceptanceRatio" stroke="#3b82f6" strokeWidth={3} dot={{ fill: '#3b82f6', strokeWidth: 2, r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900">{t('statsDashboard.sections.completionRatesByCompany')}</h3>
                <p className="mt-1 mb-6 text-sm text-gray-500">{t('statsDashboard.descriptions.completionRatesByCompany', { scope: bookingStatusScope })}</p>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={bookingPerformance?.bookingCompletionRates?.byCompany?.slice(0, 10) || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="companyName" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="completionRate" stroke="#10b981" strokeWidth={3} dot={{ fill: '#10b981', strokeWidth: 2, r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900">{t('statsDashboard.sections.seasonalTrends')}</h3>
                <p className="mt-1 mb-6 text-sm text-gray-500">{t('statsDashboard.descriptions.seasonalTrends')}</p>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">{t('statsDashboard.subsections.peakMonths')}</h4>
                    <div className="flex flex-wrap gap-2">
                      {bookingTrends?.seasonalTrends?.peakMonths?.length > 0 ? (
                        bookingTrends.seasonalTrends.peakMonths.map((month, index) => (
                          <span key={index} className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">
                            {month}
                          </span>
                        ))
                      ) : (
                        <span className="px-3 py-1 bg-gray-100 text-gray-500 text-sm rounded-full">—</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">{t('statsDashboard.subsections.lowMonths')}</h4>
                    <div className="flex flex-wrap gap-2">
                      {bookingTrends?.seasonalTrends?.lowMonths?.length > 0 ? (
                        bookingTrends.seasonalTrends.lowMonths.map((month, index) => (
                          <span key={index} className="px-3 py-1 bg-red-100 text-red-800 text-sm rounded-full">
                            {month}
                          </span>
                        ))
                      ) : (
                        <span className="px-3 py-1 bg-gray-100 text-gray-500 text-sm rounded-full">—</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">{t('statsDashboard.subsections.weekendPeak')}</h4>
                    <span className={`px-3 py-1 text-sm rounded-full ${bookingTrends?.seasonalTrends?.weekendPeak ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                      {bookingTrends?.seasonalTrends?.weekendPeak ? t('statsDashboard.filters.yes') : t('statsDashboard.filters.no')}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.performanceSummary')}</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{t('statsDashboard.labels.overallAcceptanceRatio')}</span>
                    <span className="font-medium text-gray-900">{bookingPerformance?.acceptanceRatios?.overall || '0'}%</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <div>
                      <span className="text-sm text-gray-600">{t('statsDashboard.labels.averageResponseTime')}</span>
                      {/* <p className="mt-0.5 text-xs text-gray-400">{t('statsDashboard.descriptions.averageResponseTime')}</p> */}
                    </div>
                    <span className="font-medium text-gray-900 whitespace-nowrap ml-4">{bookingPerformance?.responseTimes?.average || '0'} hours</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{t('statsDashboard.labels.overallCompletionRate')}</span>
                    <span className="font-medium text-gray-900">{bookingPerformance?.bookingCompletionRates?.overall || '0'}%</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{t('statsDashboard.labels.averageCustomerRating')}</span>
                    <span className="font-medium text-gray-900">{bookingPerformance?.customerSatisfaction?.averageRating || '0'}/5</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.hourlyBookingDistribution')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={bookingTrends?.hourlyDistribution || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="hour" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="bookingCount" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.weeklyBookingDistribution')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={bookingTrends?.weeklyDistribution || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="day" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="bookingCount" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {params?.t === 6 && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title={t('statsDashboard.tables.totalTransactions')}
                value={transactionStats?.totalTransactions?.toLocaleString() || '0'}
                change={transactionStats?.transactionSuccessRate ? `${transactionStats.transactionSuccessRate}% success rate` : null}
                icon="💳"
                trend="up"
              />
              <StatCard
                title={t('statsDashboard.cards.successfulTransactions')}
                value={transactionStats?.successfulTransactions?.toLocaleString() || '0'}
                change={pctOfTotalFrom(transactionStats?.successfulTransactions, transactionStats?.totalTransactions)}
                icon="✅"
                trend="up"
              />
              <StatCard
                title={t('statsDashboard.cards.failedTransactions')}
                value={transactionStats?.failedTransactions?.toLocaleString() || '0'}
                change={pctOfTotalFrom(transactionStats?.failedTransactions, transactionStats?.totalTransactions)}
                icon="❌"
                trend="down"
              />
              <StatCard
                title={t('statsDashboard.cards.pendingTransactions')}
                value={transactionStats?.pendingTransactions?.toLocaleString() || '0'}
                change={pctOfTotalFrom(transactionStats?.pendingTransactions, transactionStats?.totalTransactions)}
                icon="⏳"
                trend="neutral"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title={t('statsDashboard.cards.totalRevenue')}
                value={revenueStats?.totalRevenue?.toLocaleString() || '0'}
                change={t('statsDashboard.units.omr')}
                icon="💰"
                trend="up"
              />
              <StatCard
                title={t('statsDashboard.labels.netRevenue')}
                value={revenueStats?.netRevenue?.toLocaleString() || '0'}
                change={t('statsDashboard.units.omr')}
                icon="💵"
                trend="up"
              />
              <StatCard
                title={t('statsDashboard.cards.avgTransactionValue')}
                value={transactionStats?.averageTransactionValue?.toLocaleString() || '0'}
                change={t('statsDashboard.units.omr')}
                icon="📊"
                trend="up"
              />
              <StatCard
                title={t('statsDashboard.cards.transactionSuccessRate')}
                value={transactionStats?.transactionSuccessRate || '0'}
                change={t('statsDashboard.units.percent')}
                icon="📈"
                trend="up"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title={t('statsDashboard.cards.monthlyGrowth')}
                value={revenueStats?.revenueGrowth?.monthlyGrowth || '0'}
                change={t('statsDashboard.units.percent')}
                icon="📈"
                trend={growthTrend(revenueStats?.revenueGrowth?.monthlyGrowth)}
              />
              <StatCard
                title={t('statsDashboard.cards.quarterlyGrowth')}
                value={revenueStats?.revenueGrowth?.quarterlyGrowth || '0'}
                change={t('statsDashboard.units.percent')}
                icon="📊"
                trend={growthTrend(revenueStats?.revenueGrowth?.quarterlyGrowth)}
              />
              <StatCard
                title={t('statsDashboard.cards.yearlyGrowth')}
                value={revenueStats?.revenueGrowth?.yearlyGrowth || '0'}
                change={t('statsDashboard.units.percent')}
                icon="🎯"
                trend={growthTrend(revenueStats?.revenueGrowth?.yearlyGrowth)}
              />
              <StatCard
                title={t('statsDashboard.tables.successRate')}
                value={transactionStats?.transactionSuccessRate || '0'}
                change={t('statsDashboard.units.percent')}
                icon="✅"
                trend="up"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.transactionStatusDistribution')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={getTransactionStatusData()} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                      {getTransactionStatusData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.paymentMethodDistribution')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={getPaymentMethodData()} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                      {getPaymentMethodData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.revenueByPeriod')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={getRevenueByPeriodData()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="period" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="revenue" stackId="1" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.revenueBySource')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={getRevenueBySourceData()} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                      {getRevenueBySourceData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="grid grid-cols-1  gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.topCompaniesByRevenue')}</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={getRevenueByCompanyData()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="companyName" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={3} dot={{ fill: '#10b981', strokeWidth: 2, r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.revenueByLocation')}</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={getRevenueByLocationData()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="location" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={3} dot={{ fill: '#3b82f6', strokeWidth: 2, r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.topCompaniesByTransactions')}</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.userTypes.company')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.totalTransactions')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.successful')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.failed')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.successRate')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.totalAmount')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {transactionStats?.transactionsByCompany?.slice(0, 10).map((company, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{company.companyName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{company.totalTransactions}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{company.successfulTransactions}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{company.failedTransactions}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{company.successRate}%</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${company.totalAmount?.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.revenueGrowthProjections')}</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">{t('statsDashboard.subsections.growthRates')}</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-green-600">{revenueStats?.revenueGrowth?.monthlyGrowth || 0}%</p>
                        <p className="text-sm text-gray-600">{t('statsDashboard.options.monthly')}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-blue-600">{revenueStats?.revenueGrowth?.quarterlyGrowth || 0}%</p>
                        <p className="text-sm text-gray-600">{t('statsDashboard.labels.quarterly')}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-purple-600">{revenueStats?.revenueGrowth?.yearlyGrowth || 0}%</p>
                        <p className="text-sm text-gray-600">{t('statsDashboard.options.yearly')}</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">{t('statsDashboard.subsections.projections')}</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">{t('statsDashboard.labels.nextMonth')}</span>
                        <span className="font-medium text-gray-900">${revenueStats?.revenueProjections?.nextMonth?.toLocaleString() || '0'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">{t('statsDashboard.labels.nextQuarter')}</span>
                        <span className="font-medium text-gray-900">${revenueStats?.revenueProjections?.nextQuarter?.toLocaleString() || '0'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">{t('statsDashboard.labels.nextYear')}</span>
                        <span className="font-medium text-gray-900">${revenueStats?.revenueProjections?.nextYear?.toLocaleString() || '0'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.financialSummary')}</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{t('statsDashboard.labels.grossRevenue')}</span>
                    <span className="font-medium text-gray-900">${revenueStats?.grossRevenue?.toLocaleString() || '0'}</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{t('statsDashboard.labels.netRevenue')}</span>
                    <span className="font-medium text-gray-900">${revenueStats?.netRevenue?.toLocaleString() || '0'}</span>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.labels.totalTransactionAmount')}</span>
                      <span className="font-medium text-green-600">${transactionStats?.totalTransactionAmount?.toLocaleString() || '0'}</span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.labels.failedTransactionAmount')}</span>
                      <span className="font-medium text-red-600">${transactionStats?.failedTransactionAmount?.toLocaleString() || '0'}</span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.labels.refundAmount')}</span>
                      <span className="font-medium text-orange-600">${transactionStats?.refundAmount?.toLocaleString() || '0'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.topTransactionDays')}</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={transactionStats?.topTransactionDays || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" stroke="#666" />
                  <YAxis stroke="#666" />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {params?.t === 7 && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title={t('statsDashboard.labels.totalDeliveriesLabel')}
                value={deliveryStats?.totalDeliveries?.toLocaleString() || '0'}
                change={deliveryStats?.deliveryTrends?.monthlyGrowth != null ? percentGrowthLabel(deliveryStats.deliveryTrends.monthlyGrowth) : null}
                icon="🚚"
                trend={growthTrend(deliveryStats?.deliveryTrends?.monthlyGrowth)}
              />
              <StatCard
                title={t('statsDashboard.tables.deliverToMe')}
                value={deliveryStats?.deliveriesByType?.DELIVER_TO_ME?.toLocaleString() || '0'}
                change={pctOfTotalFrom(deliveryStats?.deliveriesByType?.DELIVER_TO_ME, deliveryStats?.totalDeliveries)}
                icon="📦"
                trend="up"
              />
              <StatCard
                title={t('statsDashboard.tables.pickUpMyself')}
                value={deliveryStats?.deliveriesByType?.PICK_UP_MYSELF?.toLocaleString() || '0'}
                change={pctOfTotalFrom(deliveryStats?.deliveriesByType?.PICK_UP_MYSELF, deliveryStats?.totalDeliveries)}
                icon="🏃"
                trend="up"
              />
              <StatCard
                title={t('statsDashboard.cards.totalDeliveryCost')}
                value={deliveryStats?.deliveryCosts?.totalCost?.toLocaleString() || '0'}
                change={t('statsDashboard.units.omr')}
                icon="💰"
                trend="up"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title={t('statsDashboard.cards.averageDeliveryCost')}
                value={deliveryStats?.deliveryCosts?.averageCost?.toLocaleString() || '0'}
                change={t('statsDashboard.units.omrPerDelivery')}
                icon="📊"
                trend="neutral"
              />
              <StatCard
                title={t('statsDashboard.cards.monthlyGrowth')}
                value={deliveryStats?.deliveryTrends?.monthlyGrowth || '0'}
                change={t('statsDashboard.units.percent')}
                icon="📈"
                trend={deliveryStats?.deliveryTrends?.monthlyGrowth > 0 ? 'up' : 'down'}
              />
              <StatCard
                title={t('statsDashboard.cards.preferredType')}
                value={deliveryStats?.deliveryTrends?.preferredType === 'DELIVER_TO_ME' ? t('statsDashboard.cards.deliverToMe') : t('statsDashboard.cards.pickUpMyself')}
                change={t('statsDashboard.units.mostPopular')}
                icon="🎯"
                trend="neutral"
              />
              <StatCard
                title={t('statsDashboard.labels.costTrend')}
                value={deliveryStats?.deliveryTrends?.costTrends === 'increasing' ? t('statsDashboard.trends.increasing') : t('statsDashboard.trends.decreasing')}
                change={deliveryStats?.deliveryTrends?.costTrends === 'increasing' ? t('statsDashboard.trends.costsUp') : t('statsDashboard.trends.costsDown')}
                icon={deliveryStats?.deliveryTrends?.costTrends === 'increasing' ? '📈' : '📉'}
                trend={deliveryStats?.deliveryTrends?.costTrends === 'increasing' ? 'up' : 'down'}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.deliveryTypeDistribution')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={getDeliveryTypeData()} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                      {getDeliveryTypeData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.deliveryCostAnalysis')}</h3>
                <div className="space-y-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-green-600">${deliveryStats?.deliveryCosts?.totalCost?.toLocaleString() || '0'}</p>
                    <p className="text-sm text-gray-600">{t('statsDashboard.labels.totalDeliveryCosts')}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <p className="text-xl font-semibold text-blue-600">${formatPrice(deliveryStats?.deliveryCosts?.averageCost || 0, true)}</p>
                      <p className="text-sm text-gray-600">{t('statsDashboard.labels.averageCost')}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-semibold text-purple-600">{deliveryStats?.totalDeliveries || '0'}</p>
                      <p className="text-sm text-gray-600">{t('statsDashboard.labels.totalDeliveriesLabel')}</p>
                    </div>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.labels.costTrend')}</span>
                      <span className={`font-medium ${deliveryStats?.deliveryTrends?.costTrends === 'increasing' ? 'text-red-600' : 'text-green-600'}`}>
                        {deliveryStats?.deliveryTrends?.costTrends === 'increasing' ? '↗ Increasing' : '↘ Decreasing'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.cards.monthlyGrowth')}</span>
                      <span className={`font-medium ${deliveryStats?.deliveryTrends?.monthlyGrowth > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {deliveryStats?.deliveryTrends?.monthlyGrowth > 0 ? '+' : ''}
                        {deliveryStats?.deliveryTrends?.monthlyGrowth || '0'}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1  gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.topCompaniesByDeliveries')}</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={getDeliveryCompanyData()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="companyName" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="totalDeliveries" stroke="#10b981" strokeWidth={3} dot={{ fill: '#10b981', strokeWidth: 2, r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.topCompaniesByDeliveryCost')}</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={getDeliveryCompanyData()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="companyName" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="deliveryCost" stroke="#f59e0b" strokeWidth={3} dot={{ fill: '#f59e0b', strokeWidth: 2, r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.topDeliveryLocations')}</h3>
              <ResponsiveContainer width="100%" height={450}>
                <LineChart data={getDeliveryLocationData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="location" stroke="#666" />
                  <YAxis stroke="#666" />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="deliveryCount" stroke="#3b82f6" strokeWidth={3} dot={{ fill: '#3b82f6', strokeWidth: 2, r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.companyDeliveryPerformance')}</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.userTypes.company')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.labels.totalDeliveriesLabel')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.deliverToMe')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.pickUpMyself')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.totalCost')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.avgCost')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getDeliveryCompanyData().map((company, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{company.companyName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{company.totalDeliveries}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{company.deliverToMe}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{company.pickupMyself}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${company.deliveryCost?.toLocaleString() || '0'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          ${company.totalDeliveries > 0 ? formatPrice(company.deliveryCost / company.totalDeliveries, true) : '0.000'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.locationPerformance')}</h3>
                <div className="space-y-4">
                  <div className="overflow-y-auto max-h-64">
                    {getDeliveryLocationData().map((location, index) => (
                      <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100">
                        <div>
                          <p className="font-medium text-gray-900">{location.location}</p>
                          <p className="text-sm text-gray-500">
                            {t('statsDashboard.labels.deliveriesAndPickups', {
                              deliveries: location.deliveryCount,
                              pickups: location.pickupCount,
                            })}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-gray-900">${location.totalCost?.toLocaleString() || '0'}</p>
                          <p className="text-sm text-gray-500">
                            {t('statsDashboard.labels.avgCostShort', {
                              value: location.deliveryCount > 0 ? formatPrice(location.totalCost / location.deliveryCount, true) : '0.000',
                            })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.deliverySummary')}</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{t('statsDashboard.labels.totalDeliveriesLabel')}</span>
                    <span className="font-medium text-gray-900">{deliveryStats?.totalDeliveries?.toLocaleString() || '0'}</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{t('statsDashboard.tables.totalCost')}</span>
                    <span className="font-medium text-green-600">${deliveryStats?.deliveryCosts?.totalCost?.toLocaleString() || '0'}</span>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.tables.deliverToMe')}</span>
                      <span className="font-medium text-blue-600">{deliveryStats?.deliveriesByType?.DELIVER_TO_ME?.toLocaleString() || '0'}</span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.tables.pickUpMyself')}</span>
                      <span className="font-medium text-green-600">{deliveryStats?.deliveriesByType?.PICK_UP_MYSELF?.toLocaleString() || '0'}</span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.cards.monthlyGrowth')}</span>
                      <span className={`font-medium ${deliveryStats?.deliveryTrends?.monthlyGrowth > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {deliveryStats?.deliveryTrends?.monthlyGrowth || '0'}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.labels.costTrend')}</span>
                      <span className={`font-medium ${deliveryStats?.deliveryTrends?.costTrends === 'increasing' ? 'text-red-600' : 'text-green-600'}`}>
                        {deliveryStats?.deliveryTrends?.costTrends === 'increasing' ? t('statsDashboard.trends.increasing') : t('statsDashboard.trends.decreasing')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {params?.t === 8 && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title={t('statsDashboard.tables.totalDiscounts')}
                value={discountStats?.totalDiscounts?.toLocaleString() || '0'}
                change={discountStats?.discountTrends?.monthlyGrowth != null ? percentGrowthLabel(discountStats.discountTrends.monthlyGrowth) : null}
                icon="🎫"
                trend={growthTrend(discountStats?.discountTrends?.monthlyGrowth)}
              />
              <StatCard
                title={t('statsDashboard.tables.activeDiscounts')}
                value={discountStats?.activeDiscounts?.toLocaleString() || '0'}
                change={pctOfTotalFrom(discountStats?.activeDiscounts, discountStats?.totalDiscounts)}
                icon="🟢"
                trend="up"
              />
              <StatCard
                title={t('statsDashboard.labels.expiredDiscounts')}
                value={discountStats?.expiredDiscounts?.toLocaleString() || '0'}
                change={pctOfTotalFrom(discountStats?.expiredDiscounts, discountStats?.totalDiscounts)}
                icon="🔴"
                trend="down"
              />
              <StatCard
                title={t('statsDashboard.labels.totalSavings')}
                value={discountStats?.totalSavings?.toLocaleString() || '0'}
                change={t('statsDashboard.units.omr')}
                icon="💰"
                trend="up"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title={t('statsDashboard.labels.usageRate')}
                value={discountStats?.discountUsage?.usageRate || '0'}
                change={t('statsDashboard.units.percent')}
                icon="📊"
                trend="up"
              />
              <StatCard
                title={t('statsDashboard.cards.averageSavings')}
                value={discountStats?.discountUsage?.averageSavings?.toLocaleString() || '0'}
                change={t('statsDashboard.units.omrPerUse')}
                icon="💵"
                trend="up"
              />
              <StatCard
                title={t('statsDashboard.cards.preferredType')}
                value={discountStats?.discountTrends?.preferredType || t('statsDashboard.common.notAvailable')}
                change={t('statsDashboard.units.mostPopular')}
                icon="🎯"
                trend="neutral"
              />
              <StatCard
                title={t('statsDashboard.cards.averageDiscount')}
                value={discountStats?.discountTrends?.averageDiscount?.toLocaleString() || '0'}
                change={t('statsDashboard.units.omr')}
                icon="📈"
                trend="neutral"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.discountTypeDistribution')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={getDiscountTypeData()} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                      {getDiscountTypeData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.discountUsageOverview')}</h3>
                <div className="space-y-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-green-600">{discountStats?.discountUsage?.usageRate || '0'}%</p>
                    <p className="text-sm text-gray-600">{t('statsDashboard.labels.usageRate')}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <p className="text-xl font-semibold text-blue-600">{discountStats?.discountUsage?.totalUsed?.toLocaleString() || '0'}</p>
                      <p className="text-sm text-gray-600">{t('statsDashboard.labels.totalUsed')}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-semibold text-purple-600">{discountStats?.discountUsage?.totalValid?.toLocaleString() || '0'}</p>
                      <p className="text-sm text-gray-600">{t('statsDashboard.labels.totalValid')}</p>
                    </div>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.cards.averageSavings')}</span>
                      <span className="font-medium text-green-600">${discountStats?.discountUsage?.averageSavings?.toLocaleString() || '0'}</span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.labels.totalSavings')}</span>
                      <span className="font-medium text-blue-600">${discountStats?.totalSavings?.toLocaleString() || '0'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1  gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.topCompaniesByDiscounts')}</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={getDiscountCompanyData()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="companyName" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="totalDiscounts" stroke="#10b981" strokeWidth={3} dot={{ fill: '#10b981', strokeWidth: 2, r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.topCompaniesByActiveDiscounts')}</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={getDiscountCompanyData()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="companyName" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="activeDiscounts" stroke="#3b82f6" strokeWidth={3} dot={{ fill: '#3b82f6', strokeWidth: 2, r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.mostEffectiveDiscounts')}</h3>
                <div className="space-y-4">
                  {discountStats?.discountEffectiveness?.mostEffective?.map((discount, index) => (
                    <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100">
                      <div>
                        <p className="font-medium text-gray-900">{discount.type} Discount</p>
                        <p className="text-sm text-gray-500">{t('statsDashboard.common.idLabel', { id: discount.discountId })}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-green-600">{t('statsDashboard.labels.uses', { count: discount.usage })}</p>
                        <p className="text-sm text-gray-500">{t('statsDashboard.labels.savedAmount', { amount: discount.savings?.toLocaleString() || '0' })}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.leastEffectiveDiscounts')}</h3>
                <div className="space-y-4">
                  {discountStats?.discountEffectiveness?.leastEffective?.map((discount, index) => (
                    <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100">
                      <div>
                        <p className="font-medium text-gray-900">{discount.type} Discount</p>
                        <p className="text-sm text-gray-500">{t('statsDashboard.common.idLabel', { id: discount.discountId })}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-red-600">{t('statsDashboard.labels.uses', { count: discount.usage })}</p>
                        <p className="text-sm text-gray-500">{t('statsDashboard.labels.savedAmount', { amount: discount.savings?.toLocaleString() || '0' })}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.companyDiscountPerformance')}</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.userTypes.company')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.totalDiscounts')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.activeDiscounts')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.totalUsage')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.activeRate')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getDiscountCompanyData().map((company, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{company.companyName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{company.totalDiscounts}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{company.activeDiscounts}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{company.totalUsage}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {company.totalDiscounts > 0 ? ((company.activeDiscounts / company.totalDiscounts) * 100).toFixed(1) : '0'}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.discountSummary')}</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{t('statsDashboard.tables.totalDiscounts')}</span>
                    <span className="font-medium text-gray-900">{discountStats?.totalDiscounts?.toLocaleString() || '0'}</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{t('statsDashboard.tables.activeDiscounts')}</span>
                    <span className="font-medium text-green-600">{discountStats?.activeDiscounts?.toLocaleString() || '0'}</span>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.labels.expiredDiscounts')}</span>
                      <span className="font-medium text-red-600">{discountStats?.expiredDiscounts?.toLocaleString() || '0'}</span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.labels.totalSavings')}</span>
                      <span className="font-medium text-green-600">${discountStats?.totalSavings?.toLocaleString() || '0'}</span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.labels.usageRate')}</span>
                      <span className="font-medium text-blue-600">{discountStats?.discountUsage?.usageRate || '0'}%</span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.cards.monthlyGrowth')}</span>
                      <span className={`font-medium ${discountStats?.discountTrends?.monthlyGrowth > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {discountStats?.discountTrends?.monthlyGrowth > 0 ? '+' : ''}
                        {discountStats?.discountTrends?.monthlyGrowth || '0'}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.discountTrends')}</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">{t('statsDashboard.subsections.growthAndPerformance')}</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-green-600">{discountStats?.discountTrends?.monthlyGrowth || '0'}%</p>
                        <p className="text-sm text-gray-600">{t('statsDashboard.cards.monthlyGrowth')}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-blue-600">{discountStats?.discountTrends?.preferredType || t('statsDashboard.common.notAvailable')}</p>
                        <p className="text-sm text-gray-600">{t('statsDashboard.cards.preferredType')}</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">{t('statsDashboard.subsections.averageMetrics')}</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">{t('statsDashboard.cards.averageDiscount')}</span>
                        <span className="font-medium text-gray-900">${discountStats?.discountTrends?.averageDiscount?.toLocaleString() || '0'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">{t('statsDashboard.cards.averageSavings')}</span>
                        <span className="font-medium text-gray-900">${discountStats?.discountUsage?.averageSavings?.toLocaleString() || '0'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {params?.t === 9 && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title={t('statsDashboard.labels.totalFiles')}
                value={fileUploadStats?.totalFiles?.toLocaleString() || '0'}
                change={fileUploadStats?.uploadTrends?.monthlyGrowth != null ? percentGrowthLabel(fileUploadStats.uploadTrends.monthlyGrowth) : null}
                icon="📁"
                trend={growthTrend(fileUploadStats?.uploadTrends?.monthlyGrowth)}
              />
              <StatCard
                title={t('statsDashboard.cards.totalStorageUsed')}
                value={fileUploadStats?.totalStorageUsed || '0 Bytes'}
                change={fileUploadStats?.storageMetrics?.utilizationRate != null ? percentOfAvailable(fileUploadStats.storageMetrics.utilizationRate) : null}
                icon="💾"
                trend="up"
              />
              <StatCard
                title={t('statsDashboard.labels.filesThisMonth')}
                value={fileUploadStats?.filesThisMonth?.toLocaleString() || '0'}
                change={pctOfTotalFrom(fileUploadStats?.filesThisMonth, fileUploadStats?.totalFiles)}
                icon="📅"
                trend="up"
              />
              <StatCard
                title={t('statsDashboard.labels.filesToday')}
                value={fileUploadStats?.filesToday?.toLocaleString() || '0'}
                change={fileUploadStats?.filesThisWeek != null ? changeThisWeek(fileUploadStats.filesThisWeek) : null}
                icon="📆"
                trend="up"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title={t('statsDashboard.storage.availableStorage')}
                value={fileUploadStats?.storageMetrics?.availableStorage || '0 Bytes'}
                change={t('statsDashboard.units.totalCapacity')}
                icon="🆓"
                trend="neutral"
              />
              <StatCard
                title={t('statsDashboard.sections.storageUtilizationSection')}
                value={fileUploadStats?.storageMetrics?.utilizationRate || '0'}
                change={t('statsDashboard.units.percent')}
                icon="📊"
                trend="neutral"
              />
              <StatCard
                title={t('statsDashboard.cards.averageFileSize')}
                value={fileUploadStats?.uploadTrends?.averageFileSize || '0 Bytes'}
                change={t('statsDashboard.units.perFile')}
                icon="📏"
                trend="neutral"
              />
              <StatCard
                title={t('statsDashboard.labels.largestFile')}
                value={fileUploadStats?.storageMetrics?.largestFile ? `${(fileUploadStats.storageMetrics.largestFile / (1024 * 1024)).toFixed(2)} MB` : '0 Bytes'}
                change={t('statsDashboard.units.biggestFile')}
                icon="🔝"
                trend="neutral"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.filesByType')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={getFileTypeData()} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                      {getFileTypeData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.storageByType')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={getStorageByTypeData()} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                      {getStorageByTypeData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.storageUtilizationSection')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={getStorageUtilizationData()} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                      {getStorageUtilizationData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.uploadTrends')}</h3>
                <div className="space-y-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-green-600">{fileUploadStats?.uploadTrends?.monthlyGrowth || '0'}%</p>
                    <p className="text-sm text-gray-600">{t('statsDashboard.cards.monthlyGrowth')}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <p className="text-xl font-semibold text-blue-600">{fileUploadStats?.uploadTrends?.averageFileSize || '0 Bytes'}</p>
                      <p className="text-sm text-gray-600">{t('statsDashboard.cards.averageFileSize')}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-semibold text-purple-600">{fileUploadStats?.filesThisWeek || '0'}</p>
                      <p className="text-sm text-gray-600">{t('statsDashboard.labels.filesThisWeek')}</p>
                    </div>
                  </div>
                  <div className="border-t pt-4">
                    <h4 className="font-medium text-gray-700 mb-2">{t('statsDashboard.subsections.peakUploadHours')}</h4>
                    <div className="flex flex-wrap gap-2">
                      {fileUploadStats?.uploadTrends?.peakUploadHours?.map((hour, index) => (
                        <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                          {hour}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.topCompaniesByFiles')}</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={getFileCompanyData()} layout="horizontal">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" stroke="#666" />
                    <YAxis dataKey="companyName" type="category" stroke="#666" width={120} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="totalFiles" fill="#10b981" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.topCompaniesByStorage')}</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={getFileCompanyData()} layout="horizontal">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" stroke="#666" />
                    <YAxis dataKey="companyName" type="category" stroke="#666" width={120} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="storageUsed" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.largestFiles')}</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.fileName')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.fileType')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.size')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.userTypes.company')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.uploaded')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getLargestFilesData().map((file, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{file.fileName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{file.fileType}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{(file.fileSize / (1024 * 1024)).toFixed(2)} MB</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{file.companyName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(file.uploadedAt).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.companyFilePerformance')}</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.userTypes.company')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.labels.totalFiles')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.storageUsed')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.fileTypes')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getFileCompanyData().map((company, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{company.companyName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{company.totalFiles}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{company.storageUsed} MB</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex flex-wrap gap-1">
                            {company.fileTypes?.slice(0, 3).map((type, typeIndex) => (
                              <span key={typeIndex} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                                {type.type}
                              </span>
                            ))}
                            {company.fileTypes?.length > 3 && <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">+{company.fileTypes.length - 3} more</span>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.fileUploadSummary')}</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{t('statsDashboard.labels.totalFiles')}</span>
                    <span className="font-medium text-gray-900">{fileUploadStats?.totalFiles?.toLocaleString() || '0'}</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{t('statsDashboard.labels.totalStorage')}</span>
                    <span className="font-medium text-green-600">{fileUploadStats?.totalStorageUsed || '0 Bytes'}</span>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.labels.filesThisMonth')}</span>
                      <span className="font-medium text-blue-600">{fileUploadStats?.filesThisMonth || '0'}</span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.labels.filesThisWeek')}</span>
                      <span className="font-medium text-purple-600">{fileUploadStats?.filesThisWeek || '0'}</span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.labels.filesToday')}</span>
                      <span className="font-medium text-green-600">{fileUploadStats?.filesToday || '0'}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.storageSummary')}</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{t('statsDashboard.storage.availableStorage')}</span>
                    <span className="font-medium text-gray-900">{fileUploadStats?.storageMetrics?.availableStorage || '0 Bytes'}</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{t('statsDashboard.labels.utilizationRate')}</span>
                    <span className="font-medium text-blue-600">{fileUploadStats?.storageMetrics?.utilizationRate || '0'}%</span>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.cards.averageFileSize')}</span>
                      <span className="font-medium text-green-600">{fileUploadStats?.uploadTrends?.averageFileSize || '0 Bytes'}</span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.labels.largestFile')}</span>
                      <span className="font-medium text-purple-600">
                        {fileUploadStats?.storageMetrics?.largestFile ? `${(fileUploadStats.storageMetrics.largestFile / (1024 * 1024)).toFixed(2)} MB` : '0 Bytes'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.cards.monthlyGrowth')}</span>
                      <span className={`font-medium ${fileUploadStats?.uploadTrends?.monthlyGrowth > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {fileUploadStats?.uploadTrends?.monthlyGrowth > 0 ? '+' : ''}
                        {fileUploadStats?.uploadTrends?.monthlyGrowth || '0'}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {params?.t === 10 && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title={t('statsDashboard.labels.totalFraudUsers')}
                value={fraudStats?.fraudStatistics?.totalFraudUsers?.toLocaleString() || '0'}
                change={fraudStats?.fraudTrends?.monthlyGrowth != null ? percentGrowthLabel(fraudStats.fraudTrends.monthlyGrowth) : null}
                icon="🚨"
                trend={growthTrend(fraudStats?.fraudTrends?.monthlyGrowth)}
              />
              <StatCard
                title={t('statsDashboard.cards.fraudUsersThisMonth')}
                value={fraudStats?.fraudStatistics?.fraudUsersThisMonth?.toLocaleString() || '0'}
                change={pctOfTotalFrom(fraudStats?.fraudStatistics?.fraudUsersThisMonth, fraudStats?.fraudStatistics?.totalFraudUsers)}
                icon="📅"
                trend="up"
              />
              <StatCard
                title={t('statsDashboard.cards.fraudUsersThisWeek')}
                value={fraudStats?.fraudStatistics?.fraudUsersThisWeek?.toLocaleString() || '0'}
                change={pctOfTotalFrom(fraudStats?.fraudStatistics?.fraudUsersThisWeek, fraudStats?.fraudStatistics?.totalFraudUsers)}
                icon="📆"
                trend="up"
              />
              <StatCard
                title={t('statsDashboard.cards.fraudUsersToday')}
                value={fraudStats?.fraudStatistics?.fraudUsersToday?.toLocaleString() || '0'}
                change={pctOfTotalFrom(fraudStats?.fraudStatistics?.fraudUsersToday, fraudStats?.fraudStatistics?.totalFraudUsers)}
                icon="⚠️"
                trend="up"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title={t('statsDashboard.cards.highRiskReports')}
                value={fraudStats?.riskAssessment?.highRiskReports?.toLocaleString() || '0'}
                change={fraudStats?.riskAssessment?.highRiskPercentage != null ? percentOfTotal(fraudStats.riskAssessment.highRiskPercentage) : null}
                icon="🔴"
                trend="up"
              />
              <StatCard
                title={t('statsDashboard.cards.mediumRiskReports')}
                value={fraudStats?.riskAssessment?.mediumRiskReports?.toLocaleString() || '0'}
                change={fraudStats?.riskAssessment?.mediumRiskPercentage != null ? percentOfTotal(fraudStats.riskAssessment.mediumRiskPercentage) : null}
                icon="🟡"
                trend="neutral"
              />
              <StatCard
                title={t('statsDashboard.cards.lowRiskReports')}
                value={fraudStats?.riskAssessment?.lowRiskReports?.toLocaleString() || '0'}
                change={fraudStats?.riskAssessment?.lowRiskPercentage != null ? percentOfTotal(fraudStats.riskAssessment.lowRiskPercentage) : null}
                icon="🟢"
                trend="down"
              />
              <StatCard title={t('statsDashboard.cards.totalReports')} value={fraudStats?.riskAssessment?.totalReports?.toLocaleString() || '0'} icon="📊" trend="neutral" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.fraudReportsByType')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: t('statsDashboard.fraud.detailedReport'), value: fraudStats?.fraudByType?.['Detailed Report'] || 0, fill: '#ef4444' },
                        { name: t('statsDashboard.fraud.basicReport'), value: fraudStats?.fraudByType?.['Basic Report'] || 0, fill: '#f59e0b' },
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      <Cell fill="#ef4444" />
                      <Cell fill="#f59e0b" />
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.riskAssessmentOverview')}</h3>
                <div className="space-y-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-red-600">{fraudStats?.riskAssessment?.highRiskPercentage || '0'}%</p>
                    <p className="text-sm text-gray-600">{t('statsDashboard.labels.highRisk')}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <p className="text-xl font-semibold text-yellow-600">{fraudStats?.riskAssessment?.mediumRiskPercentage || '0'}%</p>
                      <p className="text-sm text-gray-600">{t('statsDashboard.labels.mediumRisk')}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-semibold text-green-600">{fraudStats?.riskAssessment?.lowRiskPercentage || '0'}%</p>
                      <p className="text-sm text-gray-600">{t('statsDashboard.labels.lowRisk')}</p>
                    </div>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.cards.totalReports')}</span>
                      <span className="font-medium text-gray-900">{fraudStats?.riskAssessment?.totalReports?.toLocaleString() || '0'}</span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.cards.monthlyGrowth')}</span>
                      <span className={`font-medium ${fraudStats?.fraudTrends?.monthlyGrowth > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {fraudStats?.fraudTrends?.monthlyGrowth > 0 ? '+' : ''}
                        {fraudStats?.fraudTrends?.monthlyGrowth || '0'}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1  gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.topCompaniesByFraudReports')}</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={fraudStats?.fraudByCompany?.slice(0, 10) || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="companyName" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="count" stroke="#ef4444" strokeWidth={3} dot={{ fill: '#ef4444', strokeWidth: 2, r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.topReporters')}</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={fraudStats?.fraudByCreatedBy?.slice(0, 10) || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="count" stroke="#f59e0b" strokeWidth={3} dot={{ fill: '#f59e0b', strokeWidth: 2, r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.recentFraudCases')}</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.userTypes.user')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.phone')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.civilId')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.userTypes.company')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.reportedBy')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.reportedAt')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {fraudStats?.recentFraudCases?.map((case_, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{case_.userName || t('statsDashboard.common.notAvailable')}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{case_.phone || t('statsDashboard.common.notAvailable')}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{case_.civilId || t('statsDashboard.common.notAvailable')}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{case_.companyName || t('statsDashboard.common.notAvailable')}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{case_.reportedBy || t('statsDashboard.common.notAvailable')}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(case_.reportedAt).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.fraudSummary')}</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{t('statsDashboard.labels.totalFraudUsers')}</span>
                    <span className="font-medium text-gray-900">{fraudStats?.fraudStatistics?.totalFraudUsers?.toLocaleString() || '0'}</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{t('statsDashboard.labels.thisMonth')}</span>
                    <span className="font-medium text-red-600">{fraudStats?.fraudStatistics?.fraudUsersThisMonth?.toLocaleString() || '0'}</span>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.labels.thisWeek')}</span>
                      <span className="font-medium text-orange-600">{fraudStats?.fraudStatistics?.fraudUsersThisWeek?.toLocaleString() || '0'}</span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.labels.today')}</span>
                      <span className="font-medium text-red-600">{fraudStats?.fraudStatistics?.fraudUsersToday?.toLocaleString() || '0'}</span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.cards.monthlyGrowth')}</span>
                      <span className={`font-medium ${fraudStats?.fraudTrends?.monthlyGrowth > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {fraudStats?.fraudTrends?.monthlyGrowth > 0 ? '+' : ''}
                        {fraudStats?.fraudTrends?.monthlyGrowth || '0'}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.riskDistribution')}</h3>
                <div className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{t('statsDashboard.labels.highRisk')}</span>
                      <span className="font-medium text-red-600">{fraudStats?.riskAssessment?.highRiskReports?.toLocaleString() || '0'}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-red-600 h-2 rounded-full" style={{ width: `${fraudStats?.riskAssessment?.highRiskPercentage || 0}%` }}></div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{t('statsDashboard.labels.mediumRisk')}</span>
                      <span className="font-medium text-yellow-600">{fraudStats?.riskAssessment?.mediumRiskReports?.toLocaleString() || '0'}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-yellow-600 h-2 rounded-full" style={{ width: `${fraudStats?.riskAssessment?.mediumRiskPercentage || 0}%` }}></div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{t('statsDashboard.labels.lowRisk')}</span>
                      <span className="font-medium text-green-600">{fraudStats?.riskAssessment?.lowRiskReports?.toLocaleString() || '0'}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-green-600 h-2 rounded-full" style={{ width: `${fraudStats?.riskAssessment?.lowRiskPercentage || 0}%` }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {params?.t === 11 && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title={t('statsDashboard.tables.totalReviews')}
                value={reviewStats?.totalReviews?.toLocaleString() || '0'}
                change={reviewStats?.reviewTrends?.monthlyGrowth != null ? percentGrowthLabel(reviewStats.reviewTrends.monthlyGrowth) : null}
                icon="⭐"
                trend={growthTrend(reviewStats?.reviewTrends?.monthlyGrowth)}
              />
              <StatCard
                title={t('statsDashboard.tables.averageRating')}
                value={reviewStats?.averageRating?.toFixed(1) || '0.0'}
                change={
                  reviewStats?.reviewTrends?.ratingImprovement
                    ? `${reviewStats.reviewTrends.ratingImprovement > 0 ? '+' : ''}${reviewStats.reviewTrends.ratingImprovement} improvement`
                    : null
                }
                icon="📊"
                trend={reviewStats?.reviewTrends?.ratingImprovement > 0 ? 'up' : 'down'}
              />
              <StatCard
                title={t('statsDashboard.cards.reviewsThisMonth')}
                value={reviewStats?.reviewsByMonth?.slice(-1)[0]?.count || '0'}
                change={pctOfTotalFrom(reviewStats?.reviewsByMonth?.slice(-1)[0]?.count || 0, reviewStats?.totalReviews)}
                icon="📅"
                trend="up"
              />
              <StatCard
                title={t('statsDashboard.cards.reviewsWithDescription')}
                value={reviewStats?.reviewQuality?.withDescription?.toLocaleString() || '0'}
                change={pctOfTotalFrom(reviewStats?.reviewQuality?.withDescription || 0, reviewStats?.totalReviews)}
                icon="📝"
                trend="up"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title={t('statsDashboard.cards.fiveStarReviews')}
                value={reviewStats?.ratingDistribution?.['5']?.toLocaleString() || '0'}
                change={pctOfTotalFrom(reviewStats?.ratingDistribution?.['5'] || 0, reviewStats?.totalReviews)}
                icon="⭐⭐⭐⭐⭐"
                trend="up"
              />
              <StatCard
                title={t('statsDashboard.cards.fourStarReviews')}
                value={reviewStats?.ratingDistribution?.['4']?.toLocaleString() || '0'}
                change={pctOfTotalFrom(reviewStats?.ratingDistribution?.['4'] || 0, reviewStats?.totalReviews)}
                icon="⭐⭐⭐⭐"
                trend="up"
              />
              <StatCard
                title={t('statsDashboard.cards.threeStarReviews')}
                value={reviewStats?.ratingDistribution?.['3']?.toLocaleString() || '0'}
                change={pctOfTotalFrom(reviewStats?.ratingDistribution?.['3'] || 0, reviewStats?.totalReviews)}
                icon="⭐⭐⭐"
                trend="neutral"
              />
              <StatCard
                title={t('statsDashboard.cards.twoStarAndBelow')}
                value={((reviewStats?.ratingDistribution?.['2'] || 0) + (reviewStats?.ratingDistribution?.['1'] || 0)).toLocaleString()}
                change={pctOfTotalFrom((reviewStats?.ratingDistribution?.['2'] || 0) + (reviewStats?.ratingDistribution?.['1'] || 0), reviewStats?.totalReviews)}
                icon="⭐⭐"
                trend="down"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard title={t('statsDashboard.cards.websiteReviews')} value={reviewStats?.reviewsBySource?.website?.toLocaleString() || '0'} icon="🌐" trend="up" />
              <StatCard title={t('statsDashboard.cards.mobileAppReviews')} value={reviewStats?.reviewsBySource?.mobile_app?.toLocaleString() || '0'} icon="📱" trend="up" />
              <StatCard title={t('statsDashboard.cards.reviewsPerCompany')} value={reviewStats?.reviewsByCompany?.length || '0'} icon="🏢" trend="up" />
              <StatCard title={t('statsDashboard.cards.reviewsPerCar')} value={reviewStats?.reviewsByCar?.length || '0'} icon="🚗" trend="up" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.ratingDistribution')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={getReviewRatingData()} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                      {getReviewRatingData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.reviewSourceDistribution')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={getReviewSourceData()} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                      {getReviewSourceData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.reviewTrendsOverTime')}</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={getReviewTrendsData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" stroke="#666" />
                  <YAxis yAxisId="left" stroke="#666" />
                  <YAxis yAxisId="right" orientation="right" stroke="#666" />
                  <Tooltip content={<CustomTooltip />} />
                  <Line yAxisId="left" type="monotone" dataKey="count" stroke="#8884d8" strokeWidth={2} name={t('statsDashboard.labels.reviewCount')} />
                  <Line yAxisId="right" type="monotone" dataKey="averageRating" stroke="#82ca9d" strokeWidth={2} name={t('statsDashboard.cards.averageRating')} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-1  gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.topCompaniesByReviews')}</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={reviewStats?.topReviewedCompanies || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="companyName" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="reviewCount" stroke="#10b981" strokeWidth={3} dot={{ fill: '#10b981', strokeWidth: 2, r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.topCompaniesByRating')}</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={reviewStats?.reviewsByCompany?.slice(0, 10) || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="companyName" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="averageRating" stroke="#f59e0b" strokeWidth={3} dot={{ fill: '#f59e0b', strokeWidth: 2, r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.topCarsByReviews')}</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={reviewStats?.reviewsByCar?.slice(0, 10) || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="model" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="totalReviews" stroke="#3b82f6" strokeWidth={3} dot={{ fill: '#3b82f6', strokeWidth: 2, r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.topCarsByRating')}</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={reviewStats?.reviewsByCar?.slice(0, 10) || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="model" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="averageRating" stroke="#8b5cf6" strokeWidth={3} dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.companyReviewPerformance')}</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.userTypes.company')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.totalReviews')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.averageRating')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.fiveStar')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.fourStar')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.threeStar')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.twoStar')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.oneStar')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {reviewStats?.reviewsByCompany?.slice(0, 15).map((company, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{company.companyName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{company.totalReviews}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{company.averageRating}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{company.ratingDistribution?.['5'] || 0}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{company.ratingDistribution?.['4'] || 0}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{company.ratingDistribution?.['3'] || 0}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{company.ratingDistribution?.['2'] || 0}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{company.ratingDistribution?.['1'] || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.reviewQualityOverview')}</h3>
                <div className="space-y-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-green-600">{reviewStats?.reviewQuality?.withDescription?.toLocaleString() || '0'}</p>
                    <p className="text-sm text-gray-600">{t('statsDashboard.cards.reviewsWithDescription')}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <p className="text-xl font-semibold text-blue-600">{reviewStats?.reviewQuality?.withoutDescription?.toLocaleString() || '0'}</p>
                      <p className="text-sm text-gray-600">{t('statsDashboard.labels.reviewsWithoutDescription')}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-semibold text-purple-600">{reviewStats?.reviewQuality?.averageDescriptionLength || '0'}</p>
                      <p className="text-sm text-gray-600">{t('statsDashboard.labels.avgDescriptionLength')}</p>
                    </div>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.labels.descriptionRate')}</span>
                      <span className="font-medium text-green-600">
                        {reviewStats?.totalReviews ? (((reviewStats.reviewQuality?.withDescription || 0) / reviewStats.totalReviews) * 100).toFixed(1) : '0'}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.labels.qualityScore')}</span>
                      <span className="font-medium text-blue-600">
                        {reviewStats?.totalReviews
                          ? Math.min(100, ((reviewStats.reviewQuality?.withDescription || 0) / reviewStats.totalReviews) * 100 + reviewStats.averageRating * 10).toFixed(1)
                          : '0'}
                        /100
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.reviewSummary')}</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{t('statsDashboard.tables.totalReviews')}</span>
                    <span className="font-medium text-gray-900">{reviewStats?.totalReviews?.toLocaleString() || '0'}</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{t('statsDashboard.tables.averageRating')}</span>
                    <span className="font-medium text-green-600">{reviewStats?.averageRating?.toFixed(1) || '0.0'}/5</span>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.cards.monthlyGrowth')}</span>
                      <span className={`font-medium ${reviewStats?.reviewTrends?.monthlyGrowth > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {reviewStats?.reviewTrends?.monthlyGrowth > 0 ? '+' : ''}
                        {reviewStats?.reviewTrends?.monthlyGrowth || '0'}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.labels.ratingImprovement')}</span>
                      <span className={`font-medium ${reviewStats?.reviewTrends?.ratingImprovement > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {reviewStats?.reviewTrends?.ratingImprovement > 0 ? '+' : ''}
                        {reviewStats?.reviewTrends?.ratingImprovement || '0'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.labels.topRating')}</span>
                      <span className="font-medium text-purple-600">
                        {reviewStats?.reviewsByCompany?.reduce((max, company) => Math.max(max, company.averageRating), 0).toFixed(1) || '0.0'}/5
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {params?.t === 12 && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.notificationStatistics')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                <StatCard
                  title={t('statsDashboard.cards.totalNotifications')}
                  value={notificationStats?.totalNotifications?.toLocaleString() || '0'}
                  change={notificationStats?.notificationTrends?.monthlyGrowth != null ? percentGrowthLabel(notificationStats.notificationTrends.monthlyGrowth) : null}
                  icon="📢"
                  trend={growthTrend(notificationStats?.notificationTrends?.monthlyGrowth)}
                />
                <StatCard
                  title={t('statsDashboard.cards.sentNotifications')}
                  value={notificationStats?.sentNotifications?.toLocaleString() || '0'}
                  change={pctOfTotalFrom(notificationStats?.sentNotifications, notificationStats?.totalNotifications)}
                  icon="📤"
                  trend="up"
                />
                <StatCard
                  title={t('statsDashboard.cards.readNotifications')}
                  value={notificationStats?.readNotifications?.toLocaleString() || '0'}
                  change={pctOfTotalFrom(notificationStats?.readNotifications, notificationStats?.totalNotifications)}
                  icon="✅"
                  trend="up"
                />
                <StatCard
                  title={t('statsDashboard.cards.unreadNotifications')}
                  value={notificationStats?.unreadNotifications?.toLocaleString() || '0'}
                  change={pctOfTotalFrom(notificationStats?.unreadNotifications, notificationStats?.totalNotifications)}
                  icon="📬"
                  trend="down"
                />
                <StatCard
                  title={t('statsDashboard.cards.failedNotifications')}
                  value={notificationStats?.failedNotifications?.toLocaleString() || '0'}
                  change={pctOfTotalFrom(notificationStats?.failedNotifications, notificationStats?.totalNotifications)}
                  icon="❌"
                  trend="down"
                />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.activityLogStatistics')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                  title={t('statsDashboard.cards.totalActivityLogs')}
                  value={activityLogStats?.totalActivityLogs?.toLocaleString() || '0'}
                  change={activityLogStats?.activityTrends?.monthlyGrowth != null ? percentGrowthLabel(activityLogStats.activityTrends.monthlyGrowth) : null}
                  icon="📊"
                  trend={growthTrend(activityLogStats?.activityTrends?.monthlyGrowth)}
                />
                <StatCard
                  title={t('statsDashboard.cards.logsThisMonth')}
                  value={activityLogStats?.logsThisMonth?.toLocaleString() || '0'}
                  change={pctOfTotalFrom(activityLogStats?.logsThisMonth, activityLogStats?.totalActivityLogs)}
                  icon="📅"
                  trend="up"
                />
                <StatCard
                  title={t('statsDashboard.cards.logsThisWeek')}
                  value={activityLogStats?.logsThisWeek?.toLocaleString() || '0'}
                  change={pctOfTotalFrom(activityLogStats?.logsThisWeek, activityLogStats?.totalActivityLogs)}
                  icon="📆"
                  trend="up"
                />
                <StatCard
                  title={t('statsDashboard.cards.logsToday')}
                  value={activityLogStats?.logsToday?.toLocaleString() || '0'}
                  change={pctOfTotalFrom(activityLogStats?.logsToday, activityLogStats?.totalActivityLogs)}
                  icon="📝"
                  trend="up"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.deliveryStatisticsSection')}</h3>
                <div className="space-y-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-green-600">{notificationStats?.deliveryStatistics?.successRate || '0'}%</p>
                    <p className="text-sm text-gray-600">{t('statsDashboard.tables.successRate')}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <p className="text-xl font-semibold text-blue-600">{notificationStats?.deliveryStatistics?.averageDeliveryTime || '0'}</p>
                      <p className="text-sm text-gray-600">{t('statsDashboard.labels.avgDeliveryTimeMin')}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-semibold text-purple-600">{notificationStats?.notificationTrends?.preferredType || t('statsDashboard.common.notAvailable')}</p>
                      <p className="text-sm text-gray-600">{t('statsDashboard.cards.preferredType')}</p>
                    </div>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.labels.bestTimeToSend')}</span>
                      <span className="font-medium text-green-600">{notificationStats?.notificationTrends?.bestTimeToSend || t('statsDashboard.common.notAvailable')}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.engagementMetrics')}</h3>
                <div className="space-y-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-blue-600">{notificationStats?.engagementMetrics?.openRate || '0'}%</p>
                    <p className="text-sm text-gray-600">{t('statsDashboard.labels.openRate')}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <p className="text-xl font-semibold text-green-600">{notificationStats?.engagementMetrics?.clickRate || '0'}%</p>
                      <p className="text-sm text-gray-600">{t('statsDashboard.labels.clickRate')}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-semibold text-purple-600">{notificationStats?.engagementMetrics?.conversionRate || '0'}%</p>
                      <p className="text-sm text-gray-600">{t('statsDashboard.labels.conversionRate')}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.notificationSummary')}</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{t('statsDashboard.labels.totalSent')}</span>
                    <span className="font-medium text-gray-900">{notificationStats?.sentNotifications?.toLocaleString() || '0'}</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{t('statsDashboard.tables.successRate')}</span>
                    <span className="font-medium text-green-600">{notificationStats?.deliveryStatistics?.successRate || '0'}%</span>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.cards.monthlyGrowth')}</span>
                      <span className={`font-medium ${notificationStats?.notificationTrends?.monthlyGrowth > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {notificationStats?.notificationTrends?.monthlyGrowth > 0 ? '+' : ''}
                        {notificationStats?.notificationTrends?.monthlyGrowth || '0'}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.labels.openRate')}</span>
                      <span className="font-medium text-blue-600">{notificationStats?.engagementMetrics?.openRate || '0'}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.notificationsByUserType')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={getNotificationUserTypeData()} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                      {getNotificationUserTypeData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.deliveryByPlatform')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={getNotificationDeliveryData()} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                      {getNotificationDeliveryData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.activityByUserType')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={getActivityLogUserTypeData()} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                      {getActivityLogUserTypeData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.activityByStatus')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={getActivityLogStatusData()} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                      {getActivityLogStatusData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.activityByPaymentStatus')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={getActivityLogPaymentStatusData()} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                      {getActivityLogPaymentStatusData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.topCompaniesByNotifications')}</h3>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={notificationStats?.notificationsByCompany?.slice(0, 10) || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="companyName" stroke="#666" />
                  <YAxis stroke="#666" />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="totalSent" stroke="#10b981" strokeWidth={3} dot={{ fill: '#10b981', strokeWidth: 2, r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.topCompaniesByActivity')}</h3>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={activityLogStats?.activityTrends?.mostActiveCompanies?.slice(0, 10) || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="companyName" stroke="#666" />
                  <YAxis stroke="#666" />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="activityCount" stroke="#3b82f6" strokeWidth={3} dot={{ fill: '#3b82f6', strokeWidth: 2, r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.companyActivityPerformance')}</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.userTypes.company')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.totalLogs')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.successful')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.errors')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.pending')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('statsDashboard.tables.successRate')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {activityLogStats?.logsByCompany?.slice(0, 10).map((company, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{company.companyName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{company.totalLogs}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{company.successfulLogs}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{company.errorLogs}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{company.pendingLogs}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{company.successRate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.activitySummary')}</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{t('statsDashboard.cards.totalActivityLogs')}</span>
                    <span className="font-medium text-gray-900">{activityLogStats?.totalActivityLogs?.toLocaleString() || '0'}</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{t('statsDashboard.labels.thisMonth')}</span>
                    <span className="font-medium text-blue-600">{activityLogStats?.logsThisMonth?.toLocaleString() || '0'}</span>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.labels.thisWeek')}</span>
                      <span className="font-medium text-green-600">{activityLogStats?.logsThisWeek?.toLocaleString() || '0'}</span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.labels.today')}</span>
                      <span className="font-medium text-purple-600">{activityLogStats?.logsToday?.toLocaleString() || '0'}</span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-600">{t('statsDashboard.cards.monthlyGrowth')}</span>
                      <span className={`font-medium ${activityLogStats?.activityTrends?.monthlyGrowth > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {activityLogStats?.activityTrends?.monthlyGrowth > 0 ? '+' : ''}
                        {activityLogStats?.activityTrends?.monthlyGrowth || '0'}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.sections.peakActivityHours')}</h3>
                <div className="space-y-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-blue-600">{t('statsDashboard.labels.peakHours')}</p>
                    <p className="text-sm text-gray-600">{t('statsDashboard.labels.mostActiveTimes')}</p>
                  </div>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {activityLogStats?.activityTrends?.peakActivityHours?.map((hour, index) => (
                      <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                        {hour}
                      </span>
                    ))}
                  </div>
                  <div className="border-t pt-4">
                    <div className="text-center">
                      <p className="text-sm text-gray-600">{t('statsDashboard.labels.activityPeakHint')}</p>
                      <p className="text-xs text-gray-500 mt-1">{t('statsDashboard.labels.maintenanceOffPeakHint')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {params?.t === 13 && (
          <div className="space-y-6">
            {operationFunnelData?.ga4Configured === false && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                {t('statsDashboard.operationFunnel.ga4NotConfigured')}
                {operationFunnelData?.ga4Error ? ` (${operationFunnelData.ga4Error})` : ''}
              </div>
            )}

            {operationFunnelLoading && !operationFunnelData ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600"></div>
              </div>
            ) : (
              <>
                <div className="relative">
                  {operationFunnelLoading ? (
                    <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/70">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                    </div>
                  ) : null}
                  <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ${operationFunnelLoading ? 'opacity-40 pointer-events-none' : ''}`}>
                    <StatCard
                      title={t('statsDashboard.operationFunnel.activeUsers')}
                      value={formatFunnelMetric(operationFunnelSummary?.activeUsers)}
                      description={t('statsDashboard.operationFunnel.activeUsersHint')}
                      icon={
                        <div className="bg-blue-500 rounded-md p-3">
                          <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          </svg>
                        </div>
                      }
                    />
                    <StatCard
                      title={t('statsDashboard.operationFunnel.newUsers')}
                      value={formatFunnelMetric(operationFunnelSummary?.newUsers)}
                      description={t('statsDashboard.operationFunnel.newUsersHint')}
                      icon={
                        <div className="bg-orange-500 rounded-md p-3">
                          <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                            />
                          </svg>
                        </div>
                      }
                    />
                    <StatCard
                      title={t('statsDashboard.operationFunnel.carViews')}
                      value={formatFunnelMetric(operationFunnelSummary?.carViews)}
                      description={t('statsDashboard.operationFunnel.carViewsHint')}
                      icon={
                        <div className="bg-yellow-400 rounded-md p-3">
                          <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            />
                          </svg>
                        </div>
                      }
                    />
                    <StatCard
                      title={t('statsDashboard.operationFunnel.totalBookings')}
                      value={formatFunnelMetric(operationFunnelSummary?.totalBookings)}
                      description={t('statsDashboard.operationFunnel.totalBookingsHint')}
                      icon={
                        <div className="bg-emerald-500 rounded-md p-3">
                          <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                            />
                          </svg>
                        </div>
                      }
                    />
                    <StatCard
                      title={t('statsDashboard.operationFunnel.paidBookings')}
                      value={formatFunnelMetric(operationFunnelSummary?.paidBookings)}
                      description={t('statsDashboard.operationFunnel.paidBookingsHint')}
                      icon={
                        <div className="bg-violet-600 rounded-md p-3">
                          <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                            />
                          </svg>
                        </div>
                      }
                    />
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.operationFunnel.stagesTitle')}</h3>
                  {operationFunnelLoading ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={360}>
                      <BarChart data={operationFunnelStages} layout="vertical" margin={{ left: 20, right: 200 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis type="number" stroke="#666" allowDecimals={false} />
                        <YAxis type="category" dataKey="label" stroke="#666" width={180} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="count" fill="#8884d8" radius={[0, 4, 4, 0]}>
                          <LabelList dataKey="displayCount" content={renderFunnelStageLabel} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('statsDashboard.operationFunnel.dailyTitle')}</h3>
                  {operationFunnelLoading ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                    </div>
                  ) : operationFunnelDaily.length ? (
                    <ResponsiveContainer width="100%" height={360}>
                      <LineChart data={operationFunnelDaily}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="date" stroke="#666" />
                        <YAxis stroke="#666" allowDecimals={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Line type="monotone" dataKey="activeUsers" name={t('statsDashboard.operationFunnel.activeUsers')} stroke="#2563eb" strokeWidth={2.5} dot={false} />
                        <Line type="monotone" dataKey="newUsers" name={t('statsDashboard.operationFunnel.newUsers')} stroke="#f97316" strokeWidth={2.5} dot={false} />
                        <Line type="monotone" dataKey="carViews" name={t('statsDashboard.operationFunnel.carViews')} stroke="#eab308" strokeWidth={2.5} dot={false} />
                        <Line type="monotone" dataKey="totalBookings" name={t('statsDashboard.operationFunnel.totalBookings')} stroke="#22c55e" strokeWidth={2.5} dot={false} />
                        <Line type="monotone" dataKey="paidBookings" name={t('statsDashboard.operationFunnel.paidBookings')} stroke="#7c3aed" strokeWidth={2.5} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-12">{t('statsDashboard.operationFunnel.noDailyData')}</p>
                  )}
                </div>

                <OperationFunnelLists {...getQueryDateParams()} />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default DashboardStats
